// Enhanced Help & Documentation System for EXRSIM
// Includes: Context-aware help, Role-based workflows, Knowledge base, Smart FAQ, Tooltips
// Last updated: Feb 16, 2026 — Global click-to-open native date picker (any <input type="date|datetime-local|
// time|month|week"> now opens the native picker on click/focus anywhere in the field — no calendar icon
// hunt). Light Theme (brand-safe) with Sun/Moon toggle in the top nav bar. Lessons Learned module polish:
// serial numbers auto-increment per exercise, missing required fields highlight in red with auto-scroll,
// Expand All / Collapse All + per-card chevron toggles, priority color bar on the left + Status pill on
// the right, and Category / ICS Organization / Status consolidated onto a single row on the Observations
// card. Participants: new "Languages" and "Skills / Quals" fields on the Personal Information card, plus a
// "Checked In" checkbox beside "Selected" in the collapsed list with All Check In / All Check Out bulk
// toggles. Ottawa Snow Storm exercise seeded with 10 multilingual mock participants.
// Previous updates: ICS 213 General Messages overhaul (moved under Comms Unit, dashboard renamed to
// "ICS 213 - General Messages", full CRUD, expand/collapse UX, color-coded Priority + Category
// select boxes, unified split Date + 24-hour HH:MM time, single-row and bulk WinLink-compatible CSV
// Import/Export with idempotent upsert on Subject+Date+Time, and a consolidated "Print ICS 213 Report"
// covering all expanded entries). ICS 309 Comms Log and ICS 205 Comms Freqs both gained matching CSV
// Import/Export endpoints (idempotent upserts on RadioNetwork+OpPeriod and Channel+RxFreq respectively).
// After Action Report annexes reordered to: A. Incident Tracker, B. Participants, C. Resources,
// D. Financials, E. Activity Log, F. Comms Log. Exercise Evaluations reworked to the ICS-standard
// 9 Key Areas (Command & Control, Operations, Planning, Logistics, Finance/Admin, Communication,
// Resource Management, Safety & Security, Training & Readiness) rendered as color-coded radio buttons
// (Excellent → Unacceptable + Not Assessed) with Not Assessed excluded from the Overall Rating average.
// Login/Register screens now use a full-bleed firefighters background image. "Wx Obs" menu renamed to
// "Weather Obs". GOES East/West weather feeds now auto-migrate on backend startup (self-healing).
// Feb 2026 (post-fork): DOM-safe printing across ICS forms (After Action, ICS 224/225, ICS 309, Food
// Unit, Facilities Unit, MSEL), silent 2-hour auto-refresh on the Dashboard Weather card, Map Display
// Mode auto-bounds to all shapes, standardised split Date + 24-hour HH:MM inputs everywhere,
// Organization/Liaison cross-module sync with auto-persist of `persons`, Exercise Scope (Step 4)
// cleanup, Print MSEL from list and modal, and AI Deputy IC (Claude Sonnet 4.5 + Canadian ICS)
// accessible via Settings → AI Assistant.
// Previous release notes retained below for continuity.
// Feb 19, 2026 — per-exercise Documents library (PDF/XLS/DOC/PPT/CSV/TXT/images with exec/coordinator/all
// visibility) in Settings, wizard is now 17 steps, wizard auto-saves every 60 seconds, Save Draft keeps
// user on the current step. ICS 230 Quick Assign + interactive Calendar + iCal export, ICS 204 Quick
// Assign with compact view, Two-Tier Resource Model (Parent Types + Individual Units), Safety Officer
// module rework, Agency Admin / Super Admin 2FA toggle, and sidebar reorganization.

import React, { useState, useMemo } from 'react';
import { X, Search, Book, HelpCircle, Users, CheckSquare, FileText, Info } from 'lucide-react';

// Comprehensive Help Content Database
export const helpContent = {
  // ICS Forms Documentation (Updated with all current forms)
  icsForms: {
    'ics-201': {
      number: '201',
      name: 'Incident Briefing',
      purpose: 'Provides initial incident information for response personnel',
      when: 'First operational period, updated as situation changes',
      who: 'Incident Commander or initial arriving officer',
      required: ['Incident name', 'Date/Time', 'Map/Sketch', 'Current situation', 'Initial objectives'],
      tips: [
        'Complete within first hour of incident',
        'Include clear map with incident boundaries',
        'List immediate resource needs',
        'Update as situation evolves'
      ],
      relatedForms: ['ICS 202', 'ICS 203', 'ICS 209']
    },
    'ics-202': {
      number: '202',
      name: 'Incident Objectives',
      purpose: 'Describes incident strategy, control objectives, and safety considerations for the operational period',
      when: 'Each operational period before tactics meeting',
      who: 'Incident Commander or Planning Section Chief',
      required: ['Operational period', 'Incident objectives', 'Weather forecast', 'Safety message'],
      tips: [
        'Objectives should be SMART (Specific, Measurable, Achievable, Relevant, Time-bound)',
        'Include weather information',
        'Safety message is mandatory',
        'Approved by IC before distribution'
      ],
      relatedForms: ['ICS 201', 'ICS 203', 'ICS 215']
    },
    'ics-203': {
      number: '203',
      name: 'Organization Assignment List',
      purpose: 'Displays ICS organization with assigned personnel for a specific operational period',
      when: 'Each operational period',
      who: 'Resources Unit Leader',
      required: ['Position titles', 'Names of assigned personnel', 'Contact information'],
      tips: [
        'Update for each operational period',
        'Maintain span of control (1:3 to 1:7)',
        'Include contact numbers',
        'Post in visible location'
      ],
      relatedForms: ['ICS 202', 'ICS 204', 'ICS 207']
    },
    'ics-204': {
      number: '204',
      name: 'Assignment List',
      purpose: 'Communicates division/group assignments, operations, and special instructions',
      when: 'Each operational period',
      who: 'Operations Section Chief or Planning Section Chief',
      required: ['Branch/Division', 'Resources assigned', 'Work assignments', 'Special instructions'],
      tips: [
        'One form per Division/Group',
        'Clear, specific work assignments',
        'Include safety considerations',
        'Distribute before operational period begins',
        'Use Quick Assign (right sidebar) to drag Individual Units onto Resources Assigned',
        'Quick-assigned resources render in a compact card: Image, Resource Type, Unit ID, # of Personnel'
      ],
      relatedForms: ['ICS 203', 'ICS 215', 'ICS 220']
    },
    'ics-205': {
      number: '205',
      name: 'Incident Radio Communications Plan (Comms Freqs)',
      purpose: 'Provides information on incident radio frequencies and assignments. Dashboard header is now "Comms Freqs".',
      when: 'First operational period and when changes occur',
      who: 'Communications Unit Leader',
      required: ['Zone/Group assignments', 'Channel assignments', 'Function', 'RX/TX Frequency', 'Tone/NAC', 'Mode'],
      tips: [
        'Clear channel naming',
        'Test all frequencies before use',
        'Include repeater information',
        'Update when changes occur',
        'Use "Import CSV" to bulk-load frequencies — the importer is idempotent (upserts on Channel + RxFreq)',
        'Use "Export CSV" to hand off the plan to WinLink / other radio programming tools',
        'A sample file lives at /sample_ics205_freqs.csv for testing the round-trip'
      ],
      relatedForms: ['ICS 203', 'ICS 205A', 'ICS 309']
    },
    'ics-206': {
      number: '206',
      name: 'Medical Plan',
      purpose: 'Provides information on incident medical services and transportation',
      when: 'Each operational period',
      who: 'Medical Unit Leader',
      required: ['Medical aid stations', 'Transportation', 'Hospitals', 'Medical emergency procedures'],
      tips: [
        'Include ambulance locations',
        'List hospital capabilities',
        'Specify medevac procedures',
        'Post in visible location'
      ],
      relatedForms: ['ICS 202', 'ICS 203', 'ICS 208']
    },
    'ics-207': {
      number: '207',
      name: 'Incident Organization Chart',
      purpose: 'Visual representation of the ICS organization',
      when: 'First operational period and when organization changes',
      who: 'Resources Unit Leader',
      required: ['All filled positions', 'Names of personnel', 'Reporting structure'],
      tips: [
        'Use standard ICS format',
        'Show only filled positions',
        'Include names on chart',
        'Update when personnel change'
      ],
      relatedForms: ['ICS 203', 'ICS 211']
    },
    'ics-208': {
      number: '208',
      name: 'Safety Message/Plan',
      purpose: 'Communicates specific safety hazards and measures for the operational period',
      when: 'Each operational period',
      who: 'Safety Officer',
      required: ['Incident Findings', 'Safety Message', 'Safety Plan', 'Mitigation measures', 'Emergency procedures'],
      tips: [
        'Access from Command Staff → Safety Officer (no longer under a separate "Safety" menu)',
        'Use the "Incident Findings" entries to log observed hazards in real time',
        'Safety Message and Safety Plan are first-class fields — fill both each operational period',
        'Notes field has been removed; consolidate brief observations into Incident Findings',
        'Use the Print Safety Officer Report button for a full-field audit-quality printout',
        'Review with all personnel and update as conditions change'
      ],
      relatedForms: ['ICS 202', 'ICS 206', 'ICS 215', 'ICS 215A']
    },
    'ics-209': {
      number: '209',
      name: 'Incident Status Summary',
      purpose: 'Provides decision-makers with a periodic status report',
      when: 'Daily or as requested by agency',
      who: 'Planning Section Chief or Situation Unit Leader',
      required: ['Current situation', 'Resources committed', 'Incident costs', 'Weather'],
      tips: [
        'Complete accuracy required',
        'Use consistent reporting times',
        'Include cost estimates',
        'Submit through proper channels'
      ],
      relatedForms: ['ICS 201', 'ICS 214', 'ICS 219']
    },
    'ics-210': {
      number: '210',
      name: 'Resource Status Change',
      purpose: 'Tracks changes in resource status and assignments throughout incident',
      when: 'Whenever resources are assigned, reassigned, or released',
      who: 'Resources Unit Leader',
      required: ['Resource identifier', 'Status', 'Location', 'Time of change'],
      tips: [
        'Update in real-time',
        'Track all resource movements',
        'Coordinate with Operations Section',
        'Maintain accurate resource accountability'
      ],
      relatedForms: ['ICS 203', 'ICS 211', 'ICS 215']
    },
    'ics-211': {
      number: '211',
      name: 'Incident Check-In List',
      purpose: 'Tracks all personnel and equipment resources checking in at an incident',
      when: 'Continuous throughout incident',
      who: 'Check-In Recorder at incident check-in locations',
      required: ['Personnel name', 'Agency', 'Contact info', 'Check-in time', 'Qualifications'],
      tips: [
        'Establish check-in points early',
        'Verify qualifications',
        'Track all arrivals and departures',
        'Coordinate with Resources Unit'
      ],
      relatedForms: ['ICS 203', 'ICS 207', 'ICS 210']
    },
    'ics-213': {
      number: '213',
      name: 'General Message (ICS 213 - General Messages)',
      purpose: 'Standardised incident message form. Accessible from Comms Unit → General Message with a dashboard titled "ICS 213 - General Messages". Supports full CRUD, color-coded Priority + Category, and WinLink-compatible CSV round-trip.',
      when: 'Anytime a formal message needs to be recorded, tasked, or transmitted via radio/WinLink',
      who: 'Any ICS position — most often Comms Unit, IC, or Section Chiefs',
      required: ['Incident Name (auto-populated)', 'To / From', 'Subject', 'Message', 'Date', 'Time (HH:MM 24-hour)', 'Category', 'Priority'],
      tips: [
        'Live under Comms Unit — the "ICS 213 – General Message" card in the ICS 2XX Form Series hub links straight to the dashboard',
        'Priority (Routine / Priority / Immediate / Flash) and Category (Info / Request / Directive / Ack / …) are color-coded on both the select and the list badges',
        'Use the Expand All / Collapse All buttons at the top to switch between summary rows and full message bodies',
        'Date + Time are split fields — 24-hour HH:MM (e.g., 14:30 for 2:30 PM)',
        'Per-row Export CSV button produces a single-message WinLink-compatible CSV (RFC 4180 escaped)',
        'Top-of-page bulk Import CSV / Export CSV endpoints round-trip the entire exercise; imports are idempotent (upsert on Subject + Date + Time so re-imports do not duplicate)',
        'A sample WinLink file lives at /sample_ics213_winlink.csv',
        'Use "Print ICS 213 Report" at the top for a DOM-safe consolidated print of every expanded entry'
      ],
      relatedForms: ['ICS 214', 'ICS 309']
    },
    'ics-214': {
      number: '214',
      name: 'Activity Log',
      purpose: 'Records individual activity during incident',
      when: 'Throughout incident assignment',
      who: 'All personnel',
      required: ['Date/Time', 'Notable activities', 'Personnel involved'],
      tips: [
        'Log all significant actions',
        'Use 24-hour time format',
        'Be objective and factual',
        'Sign and date each entry'
      ],
      relatedForms: ['ICS 201', 'ICS 209']
    },
    'ics-215': {
      number: '215',
      name: 'Operational Planning Worksheet',
      purpose: 'Documents resource needs and assignments for next operational period',
      when: 'During tactics meeting',
      who: 'Operations Section Chief',
      required: ['Work assignments', 'Resource requests', 'Branch/Division assignments'],
      tips: [
        'Start in tactics meeting',
        'Be specific about resources needed',
        'Include requested arrival times',
        'Coordinate with Resources Unit'
      ],
      relatedForms: ['ICS 204', 'ICS 220', 'ICS 215A']
    },
    'ics-216': {
      number: '216',
      name: 'Radio Requirements Worksheet',
      purpose: 'Documents radio communication needs for the operational period',
      when: 'Each operational period during planning',
      who: 'Communications Unit Leader',
      required: ['Radio frequencies needed', 'Number of radios', 'Assignment locations', 'Special requirements'],
      tips: [
        'Coordinate with ICS 205',
        'Consider span of control',
        'Plan for contingencies',
        'Test frequencies before distribution'
      ],
      relatedForms: ['ICS 205', 'ICS 215', 'ICS 217']
    },
    'ics-217': {
      number: '217',
      name: 'Radio Communications Resource Availability Worksheet',
      purpose: 'Tracks available communications equipment and resources',
      when: 'Planning phase and throughout incident',
      who: 'Communications Unit Leader',
      required: ['Equipment type', 'Quantity available', 'Frequency capabilities', 'Assignment status'],
      tips: [
        'Maintain current inventory',
        'Track equipment locations',
        'Monitor battery levels',
        'Document equipment failures'
      ],
      relatedForms: ['ICS 205', 'ICS 216']
    },
    'ics-218': {
      number: '218',
      name: 'Support Vehicle/Equipment Inventory',
      purpose: 'Tracks support vehicles and equipment assigned to incident',
      when: 'Throughout incident',
      who: 'Ground Support Unit Leader',
      required: ['Vehicle ID', 'Make/Model', 'Resource order number', 'Location'],
      tips: [
        'Update as vehicles arrive/depart',
        'Include maintenance status',
        'Track fuel usage',
        'Note special equipment'
      ],
      relatedForms: ['ICS 219', 'ICS 220']
    },
    'ics-220': {
      number: '220',
      name: 'Air Operations Summary',
      purpose: 'Provides summary of air operations',
      when: 'Each operational period with air resources',
      who: 'Air Operations Branch Director',
      required: ['Aircraft assignments', 'Flight hours', 'Missions completed'],
      tips: [
        'Include all air assets',
        'Track flight time accurately',
        'Note any incidents',
        'Coordinate with Operations'
      ],
      relatedForms: ['ICS 204', 'ICS 215', 'ICS 220']
    },
    'ics-221': {
      number: '221',
      name: 'Demobilization Check-Out',
      purpose: 'Tracks resource demobilization process',
      when: 'During demobilization phase',
      who: 'Personnel leaving incident',
      required: ['Resource/Personnel name', 'Section/Unit clearance', 'Departure date/time'],
      tips: [
        'Get all section clearances',
        'Return all equipment',
        'Complete personnel time',
        'Verify travel arrangements'
      ],
      relatedForms: ['ICS 211', 'ICS 221']
    },
    'ics-224': {
      number: '224',
      name: 'Crew Performance Rating',
      purpose: 'Documents crew performance evaluation during incident',
      when: 'End of assignment or demobilization',
      who: 'Supervisor or Division/Group Supervisor',
      required: ['Crew name/number', 'Performance ratings', 'Specific accomplishments', 'Areas for improvement'],
      tips: [
        'Be objective and specific',
        'Focus on performance, not personality',
        'Include positive achievements',
        'Complete before crew demobilization'
      ],
      relatedForms: ['ICS 225', 'ICS 221']
    },
    'ics-225': {
      number: '225',
      name: 'Incident Personnel Performance Rating',
      purpose: 'Evaluates individual personnel performance during incident assignment',
      when: 'End of assignment or at demobilization',
      who: 'Immediate supervisor',
      required: ['Personnel name', 'Position', 'Performance rating', 'Comments'],
      tips: [
        'Document exceptional performance',
        'Note areas for development',
        'Be fair and consistent',
        'Sign and date the form'
      ],
      relatedForms: ['ICS 224', 'ICS 221']
    },
    'ics-230': {
      number: '230',
      name: 'Daily Meeting Schedule',
      purpose: 'Documents planned meetings and briefings for the operational period; now supports interactive calendar views and external calendar export.',
      when: 'Each operational period',
      who: 'Planning Section Chief',
      required: ['Meeting name', 'Date/Time From', 'Date/Time To', 'Category', 'Location', 'Attendees'],
      tips: [
        'Use From/To date-times so meetings have a real duration on the calendar',
        'Pick a Category Color from the dropdown to colour-code events (Briefing, Planning, Tactics, Operations, etc.)',
        'Quick Assign sidebar lets you drag Personnel onto the Attendees chip list and Resources onto the meeting',
        'Switch between Day / Week / Month / Year views using the react-big-calendar at the top of the form',
        'Click any calendar event to edit, drag to move',
        'Use "Download .ics" or "Add to Google Calendar" to export the schedule (GET /api/meeting-schedules/export.ics)',
        'Allow adequate preparation time and communicate schedule widely'
      ],
      relatedForms: ['ICS 202', 'ICS 215']
    },
    'ics-232': {
      number: '232',
      name: 'Resources at Risk Summary',
      purpose: 'Identifies and tracks resources at risk from incident hazards',
      when: 'Each operational period or as risk changes',
      who: 'Planning Section Chief or Safety Officer',
      required: ['Resource identification', 'Risk description', 'Protection measures', 'Priority rating'],
      tips: [
        'Include all threatened resources',
        'Update as conditions change',
        'Coordinate with Safety Officer',
        'Prioritize protection efforts'
      ],
      relatedForms: ['ICS 202', 'ICS 208', 'ICS 209']
    },
    'ics-233': {
      number: '233',
      name: 'Incident Open Action Tracker',
      purpose: 'Tracks open action items, decisions, and follow-up items during incident',
      when: 'Throughout incident',
      who: 'Planning Section or Situation Unit',
      required: ['Action item', 'Assigned to', 'Due date', 'Status', 'Completion notes'],
      tips: [
        'Review during planning meetings',
        'Track to completion',
        'Reassign if needed',
        'Document completion'
      ],
      relatedForms: ['ICS 202', 'ICS 215', 'ICS 230']
    },
    'ics-234': {
      number: '234',
      name: 'Work Analysis Matrix',
      purpose: 'Analyzes work assignments and resource needs by operational period',
      when: 'During planning phase',
      who: 'Operations Section Chief or Planning Section Chief',
      required: ['Work assignments', 'Resources needed', 'Time requirements', 'Dependencies'],
      tips: [
        'Break work into manageable tasks',
        'Identify resource gaps early',
        'Consider operational tempo',
        'Plan for contingencies'
      ],
      relatedForms: ['ICS 215', 'ICS 204', 'ICS 220']
    },
    'ics-309': {
      number: '309',
      name: 'Communications Log',
      purpose: 'Records important communications during incident',
      when: 'Throughout incident',
      who: 'Communications Unit personnel',
      required: ['Radio Network / Operational Period', 'Date/Time', 'From/To', 'Message content', 'Action'],
      tips: [
        'Msg # auto-increments from 0001 to 9999 as you add entries',
        'Use the Action dropdown (Informational, Directive, Request, Acknowledge, Follow-up) to categorize each entry',
        'Enter Date and 24-hour HH:MM Time in split fields — no datetime-local picker',
        'Record all significant messages',
        'Be clear and concise',
        'Maintain chronological order',
        'Use the Print button to generate a DOM-safe communications log report',
        'Import CSV / Export CSV round-trip the log per exercise — the importer is idempotent (upsert on RadioNetwork + OperationalPeriod, message_log entries merged by Msg#)',
        'A sample file lives at /sample_ics309_commslog.csv for testing the round-trip'
      ],
      relatedForms: ['ICS 205', 'ICS 214']
    },
    'ics-259': {
      number: '259',
      name: 'Financial Status Summary',
      purpose: 'Provides a comprehensive cost summary across four resource categories: Crew, Equipment, Overhead, and Supplies. Each category has its own dedicated sub-form for detailed line-item tracking.',
      when: 'End of each operational period or as required by Finance/Admin Section',
      who: 'Cost Unit Leader or Finance/Administration Section Chief',
      required: ['Resource category', 'Line items with quantities', 'Unit costs', 'Total costs per category', 'Grand total'],
      tips: [
        'Complete each sub-form (Crew, Equipment, Overhead, Supplies) separately',
        'Ensure line-item totals match category summaries',
        'Include overtime and hazard pay calculations in Crew costs',
        'Track equipment usage hours accurately for billing',
        'Overhead costs include facilities, utilities, and administrative expenses',
        'Supplies should differentiate between consumables and durable goods',
        'Use the Print button to generate a consolidated financial report'
      ],
      relatedForms: ['ICS 214', 'Cost Unit', 'Time Unit', 'Procurement']
    },
    'food-unit': {
      number: 'LOG',
      name: 'Food Unit Form',
      purpose: 'Plans and tracks food service operations including meal counts, dietary requirements, and service schedules for incident personnel',
      when: 'Each operational period or when food service changes',
      who: 'Food Unit Leader (Logistics Section)',
      required: ['Meal schedule', 'Estimated headcount', 'Service location', 'Menu/catering details'],
      tips: [
        'Plan meals for all operational periods',
        'Account for dietary restrictions and allergies',
        'Coordinate with Facilities Unit for kitchen/dining setup',
        'Track actual vs. estimated headcounts for future planning',
        'Include hydration stations in hot weather operations'
      ],
      relatedForms: ['ICS 218', 'Facilities Unit']
    },
    'facilities-unit': {
      number: 'LOG',
      name: 'Facilities Unit Form',
      purpose: 'Documents facility requirements, layouts, and management for incident support including base camps, staging areas, and helibases',
      when: 'Initial setup and when facility needs change',
      who: 'Facilities Unit Leader (Logistics Section)',
      required: ['Facility type', 'Location', 'Capacity', 'Setup requirements', 'Utilities needed'],
      tips: [
        'Establish facilities before personnel arrive',
        'Include sanitation and waste management plans',
        'Coordinate with Ground Support for access routes',
        'Document facility maps for orientation',
        'Plan for ADA accessibility where applicable'
      ],
      relatedForms: ['ICS 218', 'Food Unit', 'ICS 206']
    },
    'compensation-claims': {
      number: 'FIN',
      name: 'Compensation/Claims Form',
      purpose: 'Documents injury compensation and property damage claims arising from incident operations',
      when: 'When injuries or property damage occur during the incident',
      who: 'Compensation/Claims Unit Leader (Finance/Admin Section)',
      required: ['Claimant information', 'Incident date/time', 'Description of injury/damage', 'Witness information', 'Estimated cost'],
      tips: [
        'Document claims as soon as possible after the incident',
        'Include photographs of damage when available',
        'Obtain witness statements promptly',
        'Track claim status through resolution',
        'Coordinate with Safety Officer for injury reports',
        'Maintain confidentiality of medical information'
      ],
      relatedForms: ['ICS 206', 'ICS 214', 'Cost Unit']
    },
    'cost-unit': {
      number: 'FIN',
      name: 'Cost Unit Form',
      purpose: 'Tracks and analyzes all costs associated with incident operations including personnel, equipment, materials, and contracted services',
      when: 'Continuously throughout the incident',
      who: 'Cost Unit Leader (Finance/Admin Section)',
      required: ['Cost category', 'Description', 'Quantity', 'Unit cost', 'Total', 'Accounting code'],
      tips: [
        'Establish cost tracking from incident start',
        'Use consistent cost categories across operational periods',
        'Coordinate with Procurement for purchase orders',
        'Provide daily cost estimates to IC',
        'Separate direct vs. indirect costs',
        'Archive all receipts and supporting documentation'
      ],
      relatedForms: ['ICS 259', 'Time Unit', 'Procurement', 'Compensation/Claims']
    },
    'time-unit': {
      number: 'FIN',
      name: 'Time Unit Form',
      purpose: 'Records personnel time and attendance for all incident workers, including regular hours, overtime, and hazard pay',
      when: 'Daily throughout the incident',
      who: 'Time Unit Leader (Finance/Admin Section)',
      required: ['Personnel name', 'Agency', 'Position', 'Hours worked', 'Overtime hours', 'Hazard pay eligibility'],
      tips: [
        'Verify time records daily with supervisors',
        'Track regular, overtime, and hazard pay separately',
        'Document start and end times for each shift',
        'Coordinate with Check-In (ICS 211) for personnel rosters',
        'Process time records promptly for payroll',
        'Maintain records for audit compliance'
      ],
      relatedForms: ['ICS 211', 'Cost Unit', 'ICS 259']
    },
    'procurement': {
      number: 'FIN',
      name: 'Procurement Form',
      purpose: 'Documents procurement activities for supplies, equipment, and services needed for incident operations',
      when: 'When goods or services are requested or purchased',
      who: 'Procurement Unit Leader (Finance/Admin Section)',
      required: ['Item description', 'Quantity', 'Vendor', 'Cost', 'Delivery timeline', 'Authorization'],
      tips: [
        'Follow agency procurement policies and thresholds',
        'Obtain proper authorization before purchasing',
        'Document sole-source justification when required',
        'Track delivery status for all orders',
        'Maintain vendor contact information',
        'Coordinate with Logistics for delivery locations'
      ],
      relatedForms: ['Cost Unit', 'ICS 259', 'ICS 218']
    }
  },

  // Role-Based Workflows and Checklists
  roleWorkflows: {
    'incident-commander': {
      title: 'Incident Commander',
      description: 'Overall authority and responsibility for conducting incident operations',
      responsibilities: [
        'Ensure incident safety',
        'Establish incident objectives',
        'Approve IAP',
        'Determine strategy',
        'Establish command post',
        'Interface with Agency Executives',
        'Order resources',
        'Approve media releases',
        'Approve demobilization plan'
      ],
      workflow: [
        {
          phase: 'Initial Response',
          tasks: [
            'Assess situation and establish command',
            'Ensure responder safety',
            'Complete ICS 201 (Incident Briefing)',
            'Determine incident objectives',
            'Establish ICP location',
            'Request additional resources as needed',
            'Establish Command Staff positions as needed'
          ]
        },
        {
          phase: 'Operational Period Planning',
          tasks: [
            'Review/approve ICS 202 (Incident Objectives)',
            'Attend Planning Meeting',
            'Approve Incident Action Plan (IAP)',
            'Conduct operational briefings',
            'Approve resource orders'
          ]
        },
        {
          phase: 'Ongoing Operations',
          tasks: [
            'Monitor incident status',
            'Coordinate with Agency Executives',
            'Approve significant strategy changes',
            'Ensure proper documentation',
            'Conduct debriefings'
          ]
        },
        {
          phase: 'Demobilization',
          tasks: [
            'Approve demobilization plan',
            'Ensure proper close-out procedures',
            'Conduct after-action review',
            'Ensure all forms completed'
          ]
        }
      ],
      keyForms: ['ICS 201', 'ICS 202', 'ICS 203', 'ICS 209', 'ICS 214'],
      checklist: [
        { task: 'ICS 201 completed within first hour', completed: false },
        { task: 'Command Post established', completed: false },
        { task: 'Safety Officer assigned', completed: false },
        { task: 'Incident objectives set', completed: false },
        { task: 'IAP approved for operational period', completed: false },
        { task: 'Resource requests reviewed', completed: false },
        { task: 'Media liaison established', completed: false },
        { task: 'Activity log maintained (ICS 214)', completed: false }
      ]
    },
    'operations-chief': {
      title: 'Operations Section Chief',
      description: 'Responsible for managing all tactical operations at the incident',
      responsibilities: [
        'Direct all tactical operations',
        'Develop tactical objectives',
        'Manage Operations personnel',
        'Request additional resources',
        'Coordinate with other Sections',
        'Implement IAP',
        'Ensure personnel safety',
        'Maintain Unit Activity Log'
      ],
      workflow: [
        {
          phase: 'Initial Assignment',
          tasks: [
            'Get briefing from Incident Commander',
            'Assess tactical situation',
            'Identify resource needs',
            'Establish Branches/Divisions/Groups as needed',
            'Review safety considerations'
          ]
        },
        {
          phase: 'Tactics Meeting',
          tasks: [
            'Prepare ICS 215 (Operational Planning Worksheet)',
            'Present tactical options',
            'Identify resource requirements',
            'Develop work assignments for next period'
          ]
        },
        {
          phase: 'Operational Period',
          tasks: [
            'Conduct operational briefing',
            'Distribute ICS 204 (Assignment Lists)',
            'Monitor tactical operations',
            'Adjust tactics as needed',
            'Maintain communication with all units',
            'Update ICS 214 (Activity Log)'
          ]
        }
      ],
      keyForms: ['ICS 204', 'ICS 215', 'ICS 220', 'ICS 214'],
      checklist: [
        { task: 'Received IC briefing', completed: false },
        { task: 'Tactical objectives identified', completed: false },
        { task: 'ICS 215 completed for tactics meeting', completed: false },
        { task: 'ICS 204 distributed to all divisions', completed: false },
        { task: 'Operational briefing conducted', completed: false },
        { task: 'Resource status monitored', completed: false },
        { task: 'Safety concerns addressed', completed: false },
        { task: 'Activity log current (ICS 214)', completed: false }
      ]
    },
    'planning-chief': {
      title: 'Planning Section Chief',
      description: 'Responsible for collection, evaluation, and display of incident information',
      responsibilities: [
        'Collect and analyze incident information',
        'Prepare IAP',
        'Conduct Planning Meetings',
        'Supervise Planning Section',
        'Maintain resource status',
        'Prepare demobilization plan',
        'Compile incident documentation'
      ],
      workflow: [
        {
          phase: 'Operational Period Preparation',
          tasks: [
            'Collect situation information',
            'Prepare ICS 202 (Incident Objectives)',
            'Update ICS 203 (Organization Chart)',
            'Facilitate tactics meeting',
            'Prepare ICS 204 (Assignment Lists)',
            'Complete IAP package'
          ]
        },
        {
          phase: 'Planning Meeting',
          tasks: [
            'Conduct Planning Meeting using standard agenda',
            'Present situation status',
            'Facilitate tactics discussion',
            'Develop IAP',
            'Brief IC on proposed plan'
          ]
        },
        {
          phase: 'Documentation',
          tasks: [
            'Maintain incident files',
            'Prepare ICS 209 (Incident Status Summary)',
            'Track all incident documentation',
            'Prepare final incident package'
          ]
        }
      ],
      keyForms: ['ICS 202', 'ICS 203', 'ICS 204', 'ICS 209', 'ICS 211', 'ICS 214'],
      checklist: [
        { task: 'IAP prepared for operational period', completed: false },
        { task: 'Planning meeting conducted', completed: false },
        { task: 'ICS 202 (Objectives) completed', completed: false },
        { task: 'ICS 203 (Organization) updated', completed: false },
        { task: 'ICS 204 (Assignment Lists) distributed', completed: false },
        { task: 'ICS 209 submitted as required', completed: false },
        { task: 'Resource status tracked', completed: false },
        { task: 'Activity log maintained (ICS 214)', completed: false }
      ]
    },
    'logistics-chief': {
      title: 'Logistics Section Chief',
      description: 'Responsible for all support requirements needed to facilitate effective incident management',
      responsibilities: [
        'Provide facilities, services, and materials',
        'Supervise Logistics Section',
        'Coordinate with other Sections',
        'Develop logistics plan',
        'Ensure resource accountability',
        'Order supplies and equipment',
        'Arrange ground and air transportation'
      ],
      workflow: [
        {
          phase: 'Initial Setup',
          tasks: [
            'Establish Logistics Section organization',
            'Identify immediate support needs',
            'Coordinate with Supply Unit',
            'Set up facilities (ICP, Base, Camps)',
            'Establish Communications Plan (ICS 205)',
            'Establish Medical Plan (ICS 206)'
          ]
        },
        {
          phase: 'Operational Support',
          tasks: [
            'Process resource requests',
            'Track resource status',
            'Maintain equipment inventory (ICS 218)',
            'Ensure adequate supplies',
            'Coordinate meals and lodging',
            'Arrange transportation',
            'Maintain communications equipment'
          ]
        },
        {
          phase: 'Planning Meeting Participation',
          tasks: [
            'Report logistics capabilities',
            'Identify constraints',
            'Coordinate resource delivery times',
            'Brief logistical considerations'
          ]
        }
      ],
      keyForms: ['ICS 205', 'ICS 206', 'ICS 218', 'ICS 214'],
      checklist: [
        { task: 'ICS 205 (Comm Plan) established', completed: false },
        { task: 'ICS 206 (Medical Plan) prepared', completed: false },
        { task: 'Supply Unit operational', completed: false },
        { task: 'Facilities established', completed: false },
        { task: 'Vehicle inventory tracked (ICS 218)', completed: false },
        { task: 'Resource requests processed', completed: false },
        { task: 'Food/water adequate', completed: false },
        { task: 'Activity log maintained (ICS 214)', completed: false }
      ]
    },
    'finance-admin-chief': {
      title: 'Finance/Administration Section Chief',
      description: 'Responsible for all financial and cost analysis aspects of the incident',
      responsibilities: [
        'Track incident costs',
        'Manage procurement',
        'Process timekeeping',
        'Manage compensation/claims',
        'Supervise Finance/Admin Section',
        'Provide cost estimates',
        'Coordinate cost documentation'
      ],
      workflow: [
        {
          phase: 'Section Activation',
          tasks: [
            'Establish Finance/Admin organization',
            'Set up cost tracking systems',
            'Coordinate with Logistics on procurement',
            'Brief personnel on documentation requirements',
            'Establish time reporting procedures'
          ]
        },
        {
          phase: 'Ongoing Operations',
          tasks: [
            'Track all incident costs',
            'Process time reports',
            'Handle injury/damage claims',
            'Monitor procurement procedures',
            'Prepare cost estimates',
            'Maintain financial records'
          ]
        },
        {
          phase: 'Close-Out',
          tasks: [
            'Ensure all time documented',
            'Process final claims',
            'Complete cost analysis',
            'Prepare final financial report',
            'Archive financial documentation'
          ]
        }
      ],
      keyForms: ['ICS 214', 'Cost Unit forms', 'Time Unit forms', 'Compensation/Claims forms'],
      checklist: [
        { task: 'Cost tracking system established', completed: false },
        { task: 'Time reporting procedures in place', completed: false },
        { task: 'Procurement coordinated with Logistics', completed: false },
        { task: 'Personnel briefed on documentation', completed: false },
        { task: 'Cost estimates provided to IC', completed: false },
        { task: 'Claims processed promptly', completed: false },
        { task: 'Financial records maintained', completed: false },
        { task: 'Activity log maintained (ICS 214)', completed: false }
      ]
    },
    'safety-officer': {
      title: 'Safety Officer',
      description: 'Monitors and assesses safety hazards and develops measures to ensure personnel safety',
      responsibilities: [
        'Identify and mitigate hazards',
        'Participate in planning meetings',
        'Review IAP for safety considerations',
        'Exercise emergency authority to stop unsafe acts',
        'Investigate accidents',
        'Review/approve Medical Plan',
        'Ensure accident investigations conducted'
      ],
      workflow: [
        {
          phase: 'Initial Assessment',
          tasks: [
            'Conduct safety assessment of incident site',
            'Identify immediate hazards',
            'Brief IC on safety concerns',
            'Establish safety priorities'
          ]
        },
        {
          phase: 'Operational Period Planning',
          tasks: [
            'Participate in planning meetings',
            'Review IAP for safety issues',
            'Develop safety message for ICS 202',
            'Review Medical Plan (ICS 206)',
            'Brief responders on safety considerations'
          ]
        },
        {
          phase: 'Operations Monitoring',
          tasks: [
            'Observe operations for hazards',
            'Ensure proper PPE use',
            'Monitor environmental conditions',
            'Investigate accidents/near-misses',
            'Document safety concerns',
            'Exercise stop-work authority when needed'
          ]
        }
      ],
      keyForms: ['ICS 202', 'ICS 206', 'ICS 214', 'Safety briefing forms'],
      checklist: [
        { task: 'Site safety assessment completed', completed: false },
        { task: 'IC briefed on safety concerns', completed: false },
        { task: 'Safety message in ICS 202', completed: false },
        { task: 'Medical Plan (ICS 206) reviewed', completed: false },
        { task: 'Safety briefing conducted', completed: false },
        { task: 'PPE requirements verified', completed: false },
        { task: 'Hazards monitored continuously', completed: false },
        { task: 'Activity log maintained (ICS 214)', completed: false }
      ]
    },
    'liaison-officer': {
      title: 'Liaison Officer',
      description: 'Point of contact for assisting and cooperating agency representatives',
      responsibilities: [
        'Coordinate with agency representatives',
        'Maintain list of cooperating agencies',
        'Monitor incident operations',
        'Keep agencies informed',
        'Brief agency representatives',
        'Facilitate inter-agency coordination'
      ],
      workflow: [
        {
          phase: 'Initial Contact',
          tasks: [
            'Identify all assisting/cooperating agencies',
            'Establish contact with agency representatives',
            'Provide incident briefing',
            'Set up agency representative area',
            'Establish communication protocols'
          ]
        },
        {
          phase: 'Ongoing Coordination',
          tasks: [
            'Facilitate agency communication',
            'Coordinate agency resource needs',
            'Keep agencies informed of incident status',
            'Brief new agency representatives',
            'Resolve inter-agency issues',
            'Coordinate with IC on agency matters'
          ]
        },
        {
          phase: 'Information Sharing',
          tasks: [
            'Distribute IAP to agencies',
            'Provide situation updates',
            'Coordinate agency participation in planning',
            'Document agency involvement'
          ]
        }
      ],
      keyForms: ['ICS 209', 'ICS 214', 'Agency contact lists'],
      checklist: [
        { task: 'All agencies identified', completed: false },
        { task: 'Agency representatives contacted', completed: false },
        { task: 'Contact list maintained', completed: false },
        { task: 'Agency area established', completed: false },
        { task: 'IAP distributed to agencies', completed: false },
        { task: 'Regular briefings conducted', completed: false },
        { task: 'Inter-agency coordination maintained', completed: false },
        { task: 'Activity log maintained (ICS 214)', completed: false }
      ]
    },
    'information-officer': {
      title: 'Information Officer (Public Information Officer)',
      description: 'Serves as official spokesperson and coordinates public information',
      responsibilities: [
        'Develop public information strategy',
        'Coordinate media releases',
        'Act as incident spokesperson',
        'Arrange media briefings',
        'Monitor media coverage',
        'Coordinate with agency PIOs',
        'Manage Joint Information Center if established'
      ],
      workflow: [
        {
          phase: 'Initial Setup',
          tasks: [
            'Get briefing from IC',
            'Identify media interest level',
            'Establish JIC if needed',
            'Develop initial media release',
            'Set up media briefing schedule',
            'Coordinate with agency PIOs'
          ]
        },
        {
          phase: 'Ongoing Operations',
          tasks: [
            'Monitor incident developments',
            'Prepare media updates',
            'Conduct media briefings',
            'Respond to media inquiries',
            'Monitor media coverage',
            'Coordinate social media messaging',
            'Get IC approval for releases'
          ]
        },
        {
          phase: 'Information Management',
          tasks: [
            'Maintain media contact list',
            'Document all releases',
            'Track public inquiries',
            'Ensure consistent messaging',
            'Archive media products'
          ]
        }
      ],
      keyForms: ['ICS 209', 'ICS 214', 'Media contact lists', 'Press releases'],
      checklist: [
        { task: 'IC briefing received', completed: false },
        { task: 'Media strategy developed', completed: false },
        { task: 'Initial press release issued', completed: false },
        { task: 'Media contact list established', completed: false },
        { task: 'Briefing schedule set', completed: false },
        { task: 'Agency PIOs coordinated', completed: false },
        { task: 'All releases IC-approved', completed: false },
        { task: 'Activity log maintained (ICS 214)', completed: false }
      ]
    },
    'exercise-controller': {
      title: 'Exercise Controller (ExCon)',
      description: 'Plans, manages, and controls exercise execution including MSEL injections, participant evaluation, and scenario progression',
      responsibilities: [
        'Plan and design exercise scenarios using ExCon Builder',
        'Create and manage MSEL injections',
        'Deliver injections at scheduled scenario times',
        'Monitor participant responses via ExCon Dashboard',
        'Document actual responses for post-exercise evaluation',
        'Coordinate with evaluators and observers',
        'Manage injection alert system during live exercises',
        'Conduct post-exercise After Action Review (AAR)'
      ],
      workflow: [
        {
          phase: 'Exercise Design',
          tasks: [
            'Open ExCon Builder (17-step wizard)',
            'Define exercise info, scenario, and Goals & Objectives',
            'Add participants and assign ExCon positions (Director, Evaluator, Observer)',
            'Create MSEL injections with scenario date/times and inject modes',
            'Review ExCon Team auto-populated from qualified participants',
            'Assign ExCon Lead from the dropdown',
            'Complete all 17 steps and save'
          ]
        },
        {
          phase: 'Pre-Exercise Setup',
          tasks: [
            'Verify all MSEL injections are scheduled with correct scenario date/times',
            'Confirm inject modes match exercise design (Phone, Radio, Email, In-Person, etc.)',
            'Brief ExCon Team on their roles and injection responsibilities',
            'Test ExCon Dashboard access for all team members',
            'Verify injection alert system is working (polls every 30 seconds)'
          ]
        },
        {
          phase: 'Exercise Execution',
          tasks: [
            'Open ExCon Dashboard and keep it active throughout exercise',
            'Monitor injection alerts (triggers 10 minutes before scheduled time)',
            'Acknowledge alerts and prepare injection materials',
            'Deliver injections using assigned inject mode',
            'Record actual participant responses in the Actual Response field',
            'Mark injections as completed with actual delivery time',
            'Monitor exercise progression and adjust as needed'
          ]
        },
        {
          phase: 'Post-Exercise',
          tasks: [
            'Review all injection completions and actual responses',
            'Compare expected vs. actual responses for each injection',
            'Document timing variances (scheduled vs. actual)',
            'Contribute findings to After Action Review (AAR)',
            'Update Lessons Learned based on exercise outcomes'
          ]
        }
      ],
      keyForms: ['MSEL', 'ICS 201', 'ICS 202', 'ICS 214', 'AAR'],
      checklist: [
        { task: 'ExCon Builder completed (all 17 steps)', completed: false },
        { task: 'MSEL injections created with scenario date/times', completed: false },
        { task: 'ExCon Team members assigned and briefed', completed: false },
        { task: 'ExCon Lead designated', completed: false },
        { task: 'Inject modes assigned to all injections', completed: false },
        { task: 'ExCon Dashboard accessible to all team members', completed: false },
        { task: 'Injection alert system tested', completed: false },
        { task: 'All injections delivered during exercise', completed: false },
        { task: 'Actual responses documented for all injections', completed: false },
        { task: 'After Action Review contributions completed', completed: false }
      ]
    },
    'communications-unit': {
      title: 'Communications Unit Leader',
      description: 'Prepares and implements communications plan and manages communications equipment',
      responsibilities: [
        'Develop Communications Plan (ICS 205)',
        'Manage communications equipment',
        'Maintain Communications Log (ICS 309)',
        'Supervise IT specialists',
        'Coordinate frequency assignments',
        'Test communications equipment',
        'Provide technical support'
      ],
      workflow: [
        {
          phase: 'Initial Setup',
          tasks: [
            'Assess communications needs',
            'Prepare ICS 205 (Communications Plan)',
            'Assign radio frequencies',
            'Test all communications equipment',
            'Establish communications center',
            'Brief personnel on comm procedures'
          ]
        },
        {
          phase: 'Operational Support',
          tasks: [
            'Maintain all communications equipment',
            'Monitor radio traffic',
            'Update ICS 309 (Communications Log)',
            'Troubleshoot equipment issues',
            'Coordinate frequency changes',
            'Ensure adequate equipment inventory'
          ]
        },
        {
          phase: 'Documentation',
          tasks: [
            'Maintain complete communications log',
            'Document equipment status',
            'Track frequency usage',
            'Update communications plan as needed',
            'Prepare equipment accountability records'
          ]
        }
      ],
      keyForms: ['ICS 205', 'ICS 309', 'ICS 214', 'Equipment accountability forms'],
      checklist: [
        { task: 'ICS 205 (Comm Plan) completed', completed: false },
        { task: 'All frequencies tested', completed: false },
        { task: 'Equipment inventory established', completed: false },
        { task: 'Communications center operational', completed: false },
        { task: 'Personnel briefed on procedures', completed: false },
        { task: 'ICS 309 (Comm Log) maintained', completed: false },
        { task: 'Equipment maintained/charged', completed: false },
        { task: 'Activity log maintained (ICS 214)', completed: false }
      ]
    }
  },

  // FAQ Database with categories
  faq: [
    {
      id: 1,
      category: 'Getting Started',
      question: 'How do I create a new exercise?',
      answer: 'Navigate to Exercises in the sidebar and click "Create New Exercise" to launch the 17-step Exercise Builder Wizard. Each step guides you through building a complete exercise:\n\n' +
        'Step 1: Exercise Details — Name, type (TTX, Functional, Full-Scale, Drill, Workshop), date, duration, location, and exercise images.\n' +
        'Step 2: Scenario — Describe the simulated incident, hazard type, and timeline of events.\n' +
        'Step 3: Purpose — State the intent and reason for conducting the exercise.\n' +
        'Step 4: Scope — Define boundaries (Description, Hazards, Geographic Area, Functions) and copy the exercise type from Step 1.\n' +
        'Step 5: Goals & Objectives — Add goals with Name, Broad Statement, and Objective (SMART).\n' +
        'Step 6: Functions — Select ICS functions to be exercised (Command, Operations, Planning, Logistics, Finance/Admin).\n' +
        'Step 7: Injections (MSEL) — Create Master Scenario Events List entries with timed injects for exercise play.\n' +
        'Step 8: Organizations — Add participating agencies with description, home base, contact info, and logo image.\n' +
        'Step 9: Exercise Sites — Add physical sites with image (upload / camera / drag-drop), Proposed Location, Exercise Function, and Participant Guidance.\n' +
        'Step 10: Team Coordinators — Assign exercise coordination team members (name, role, email, phone, assigned to).\n' +
        'Step 11: Code Words — Define exercise start, pause, and stop code words.\n' +
        'Step 12: Callsigns — Set up communication callsigns and radio frequencies (type, modulation, primary/backup).\n' +
        'Step 13: Assumptions — Document exercise assumptions and ground rules.\n' +
        'Step 14: Artificialities — List artificial constraints that differ from real-world operations.\n' +
        'Step 15: Safety — Upload a Safety Image and document Safety Concerns.\n' +
        'Step 16: Standard of Conduct — Add standards (Standard Name, Conduct Expected) for participants.\n' +
        'Step 17: Mapping — Create map objects (markers, polygons, routes) for the exercise area.\n\n' +
        'The wizard auto-saves every 60 seconds once an exercise is created, so nothing is lost. Use the step navigator on the left to jump between steps, or Save Draft on any step to stay put.',
      keywords: ['create', 'exercise', 'new', 'start', 'begin', 'wizard', '17 step', 'builder', 'scope', 'scenario', 'goals', 'objectives', 'injections', 'MSEL', 'mapping', 'sites', 'standard of conduct']
    },
    {
      id: 2,
      category: 'ICS Forms',
      question: 'What is the difference between ICS 201 and ICS 202?',
      answer: 'ICS 201 is the Incident Briefing completed in the first operational period. ICS 202 is Incident Objectives for each subsequent operational period. The 201 provides initial situation; 202 provides specific objectives for upcoming period.',
      keywords: ['ics 201', 'ics 202', 'difference', 'objectives', 'briefing']
    },
    {
      id: 3,
      category: 'ICS Forms',
      question: 'When should I complete ICS 214?',
      answer: 'ICS 214 (Activity Log) should be maintained throughout your entire assignment. Record significant activities, decisions, and events as they occur. This is a personal log for all incident personnel.',
      keywords: ['ics 214', 'activity log', 'when', 'complete']
    },
    {
      id: 4,
      category: 'Resources',
      question: 'How do I assign resources to an exercise?',
      answer: 'Go to Emergency Management → Resources → Select the resource → Check "Assigned Exercise" dropdown and select your exercise → Check "This resource is involved in the current exercise" → Click "Update Resource".',
      keywords: ['resources', 'assign', 'exercise', 'add']
    },
    {
      id: 5,
      category: 'Printing',
      question: 'How do I print ICS forms?',
      answer: 'Each ICS form has a "Print" button in the top-right corner. Click this to generate a properly formatted PDF that follows official ICS standards. You can print individual forms or complete IAP packages.',
      keywords: ['print', 'pdf', 'export', 'forms']
    },
    {
      id: 6,
      category: 'ICS Forms',
      question: 'What is an IAP and what forms are included?',
      answer: 'An Incident Action Plan (IAP) is the collection of forms that documents incident objectives and tactics for an operational period. A basic IAP includes: ICS 202 (Objectives), ICS 203 (Organization), ICS 204 (Assignment Lists), ICS 205 (Communications Plan), and ICS 206 (Medical Plan).',
      keywords: ['iap', 'incident action plan', 'forms', 'package']
    },
    {
      id: 7,
      category: 'Navigation',
      question: 'How do I switch between exercises?',
      answer: 'Click on the current exercise name in the top navigation bar. This opens the exercise selector where you can choose a different exercise from your list.',
      keywords: ['switch', 'change', 'exercise', 'navigate']
    },
    {
      id: 8,
      category: 'Date/Time',
      question: 'What time format should I use?',
      answer: 'EXRSIM uses split Date + 24-hour Time (HH:MM) inputs on every form (ICS 206, 224, 225, 259 series, 309, MSEL, Meeting Schedule, Food Unit, Facilities Unit, etc.). Enter the date in the date picker and the time in a plain HH:MM text field — for example, 2:30 PM is entered as 14:30. The old native datetime-local browser pickers have been retired everywhere so the layout is consistent across desktop, tablet, and mobile.\n\nAs of Feb 16, 2026, every Date / Time / DateTime / Month / Week input in EXRSIM opens its native picker the moment you click (or tab-focus into) the field — anywhere in the field, not just on the calendar icon. The native icon is hidden; the cursor turns into a pointer so the whole field reads as clickable.',
      keywords: ['time', 'format', '24 hour', 'date', 'split', 'hh:mm', 'datetime-local', 'date picker', 'click to open', 'calendar icon']
    },
    {
      id: 9,
      category: 'Technical',
      question: 'My changes are not saving. What should I do?',
      answer: 'Ensure you have an active internet connection. Click the "Save" button on the form. Check for red validation error messages. If the problem persists, refresh the page and try again.',
      keywords: ['save', 'not saving', 'problem', 'error']
    },
    {
      id: 10,
      category: 'ICS Concepts',
      question: 'What is Span of Control?',
      answer: 'Span of Control refers to the number of individuals a supervisor can effectively manage. ICS recommends a ratio of 1:5 (one supervisor to five subordinates), with an acceptable range of 1:3 to 1:7.',
      keywords: ['span of control', 'supervision', 'ratio', 'management']
    },
    {
      id: 11,
      category: 'ICS Concepts',
      question: 'What is an Operational Period?',
      answer: 'An Operational Period is the period of time scheduled for execution of a given set of operation actions, as specified in the IAP. Operational periods can be of various lengths, but typically 12 or 24 hours.',
      keywords: ['operational period', 'period', 'iap', 'time']
    },
    {
      id: 12,
      category: 'Weather',
      question: 'How do I view weather information for my exercise?',
      answer: 'Navigate to Exercise → ICS Dashboard. The Weather card displays live conditions and silently auto-refreshes every 2 hours, so the display stays current without any user action. For deeper investigation open the Weather Module (Settings → Weather) — the drop-downs are now "GOES East / West" and "GOES Image" and the feed list is the curated set of GOES East/West satellite regions (Full Disk, CONUS, Canadian regions, Arctic, etc.). See the "GOES Satellite Weather" FAQ for the complete image catalogue.',
      keywords: ['weather', 'forecast', 'conditions', 'goes', 'auto refresh', '2 hours']
    },
    {
      id: 13,
      category: 'HIRA',
      question: 'What is HIRA and how do I use it?',
      answer: 'HIRA (Hazard Identification and Risk Assessment) helps identify potential hazards and assess their risks. Navigate to Emergency Management → HIRA to document hazards, evaluate risks, and develop mitigation strategies.',
      keywords: ['hira', 'hazard', 'risk', 'assessment']
    },
    {
      id: 14,
      category: 'Personnel',
      question: 'How do I add personnel to my exercise?',
      answer: 'Go to Emergency Management → Personnel → Create Personnel. Fill in their information and assign them to your exercise. You can then assign them to ICS positions in ICS 203.',
      keywords: ['personnel', 'add', 'staff', 'people']
    },
    {
      id: 15,
      category: 'Forms',
      question: 'Can I edit a form after saving it?',
      answer: 'Yes, most forms can be edited. Find the saved form in the list view, click "Edit", make your changes, and click "Update" or "Save" again.',
      keywords: ['edit', 'update', 'change', 'modify']
    },
    {
      id: 16,
      category: 'ExCon & Builders',
      question: 'What is the ExCon Dashboard?',
      answer: 'The ExCon Dashboard is a centralized hub for Exercise Controllers to manage MSEL injections across all exercises. It shows summary cards (total exercises, injections, completed, pending), a filterable exercise list, and tools to add, edit, and track injections. It auto-refreshes every 60 seconds and includes an injection alert system.',
      keywords: ['excon', 'dashboard', 'injection', 'exercise control', 'msel']
    },
    {
      id: 17,
      category: 'ExCon & Builders',
      question: 'Who can access the ExCon Dashboard?',
      answer: 'Only Super Admins and participants assigned as Exercise Director, Exercise Evaluator, or Exercise Observer can see the ExCon Dashboard in the sidebar. Non-ExCon participants will not see it. This is Role-Based Access Control (RBAC) to protect exercise scenario details.',
      keywords: ['access', 'rbac', 'role', 'permission', 'excon', 'director', 'evaluator', 'observer']
    },
    {
      id: 18,
      category: 'ExCon & Builders',
      question: 'What is the difference between Exercise Builder and ExCon Builder?',
      answer: 'Both are 17-step wizards with the same structure. The ExCon Builder is designed specifically for Exercise Controllers: it auto-populates the ExCon Team from qualified participants, provides an ExCon Lead dropdown (instead of a text field), and is linked to HIRA entries for context.',
      keywords: ['excon builder', 'exercise builder', 'difference', '17 step', 'wizard']
    },
    {
      id: 19,
      category: 'ExCon & Builders',
      question: 'What is the Event Builder?',
      answer: 'The Event Builder is a streamlined 5-step wizard for real-world emergency events: Event Info, Scenario, Organizations, Mapping, and Activate ICS. It provides quick setup for actual incidents and links to HIRA entries for hazard context.',
      keywords: ['event builder', 'real event', 'emergency', '5 step', 'activate ics']
    },
    {
      id: 20,
      category: 'ExCon & Builders',
      question: 'How does the injection alert system work?',
      answer: 'The ExCon Dashboard polls every 30 seconds for upcoming injections. When a scenario date/time is within 10 minutes of the current system time, an alert modal appears showing the injection details (date/time, event type, from, to). Click "Acknowledge" to dismiss. Acknowledged injections will not re-alert.',
      keywords: ['alert', 'injection', 'upcoming', 'notification', '10 minutes', 'acknowledge']
    },
    {
      id: 21,
      category: 'ExCon & Builders',
      question: 'What is the "Actual Response" field on injections?',
      answer: 'The Actual Response field allows ExCon Team members to document what participants actually did in response to an injection. This is used for post-exercise evaluation and the After Action Review (AAR) to compare expected vs. actual responses.',
      keywords: ['actual response', 'injection', 'response', 'aar', 'evaluation']
    },
    {
      id: 22,
      category: 'ExCon & Builders',
      question: 'What Inject Modes are available?',
      answer: 'When creating or editing an injection, you can choose from: Phone Call, Radio Communication, Email/Message, In-Person, Simulation Cell, Observer Note, Social Media, and News Report. This defines how the injection is delivered to participants.',
      keywords: ['inject mode', 'phone', 'radio', 'email', 'in-person', 'simcell', 'delivery']
    },
    {
      id: 23,
      category: 'ExCon & Builders',
      question: 'Why can\'t I see injections in my exercise?',
      answer: 'Injection visibility is restricted to ExCon Team members. You must be assigned as Exercise Director, Exercise Evaluator, or Exercise Observer in that specific exercise. Super Admins always have access. This Role-Based Access Control protects scenario details from participants.',
      keywords: ['injections', 'hidden', 'not visible', 'access', 'restricted', 'role']
    },
    {
      id: 24,
      category: 'ExCon & Builders',
      question: 'How do I add an injection from the ExCon Dashboard?',
      answer: 'Navigate to ExCon Dashboard (sidebar), expand the target exercise, click "+ Add Injection", fill in the form (Scenario Date/Time, Event Type, Inject Mode, From/To, Message, Expected Response), and click Save. The injection is automatically sorted by date/time.',
      keywords: ['add', 'injection', 'excon dashboard', 'create', 'msel']
    },
    {
      id: 25,
      category: 'ExCon & Builders',
      question: 'What are the ExCon Team positions?',
      answer: 'The ExCon Team consists of participants assigned these positions: Exercise Director (leads the exercise control), Exercise Evaluator (assesses participant performance), and Exercise Observer (observes and documents). These positions grant access to the ExCon Dashboard and injection data.',
      keywords: ['excon team', 'director', 'evaluator', 'observer', 'position', 'role']
    },
    {
      id: 26,
      category: 'Help Desk',
      question: 'What is the Help Desk and how do I access it?',
      answer: 'The Help Desk is a private ticket system for reporting issues, requesting support, or making suggestions. Access it from the sidebar menu at the bottom (look for the "Help Desk" item with a question-mark icon). All users can submit tickets; only the submitter and Super Admins can view them.',
      keywords: ['help desk', 'support', 'ticket', 'access', 'sidebar']
    },
    {
      id: 27,
      category: 'Help Desk',
      question: 'How do I submit a Help Desk ticket?',
      answer: 'Navigate to Help Desk in the sidebar, click "New Ticket", fill in Subject (required), Description, Recommendation, and Priority (Low, Medium, High, Critical). Click "Create Ticket" to submit. Your ticket is private and visible only to you and Super Admins.',
      keywords: ['submit', 'create', 'ticket', 'help desk', 'new', 'priority']
    },
    {
      id: 28,
      category: 'Help Desk',
      question: 'What can Super Admins do with Help Desk tickets?',
      answer: 'Super Admins can view all tickets from all users, edit any ticket (add a Response, change Status or Priority), delete tickets, and print ticket reports. Use the search bar and status/priority filters to find specific tickets. Click on a ticket to view its full details and respond.',
      keywords: ['super admin', 'manage', 'edit', 'delete', 'print', 'response', 'help desk']
    },
    {
      id: 29,
      category: 'Help Desk',
      question: 'What are the ticket statuses and priorities?',
      answer: 'Statuses: Open (new ticket), In Progress (being worked on), Resolved (fix applied), Closed (completed), Cancelled (no longer needed). Priorities: Low, Medium, High, Critical. Super Admins update these fields as they work through tickets.',
      keywords: ['status', 'priority', 'open', 'in progress', 'resolved', 'closed', 'critical', 'help desk']
    },
    {
      id: 30,
      category: 'Analytics',
      question: 'Where is the Analytics Dashboard and what does it show?',
      answer: 'The Analytics Dashboard is inside the Exercise Management view. Click on an exercise, then navigate to the Analytics section from the exercise sidebar. It displays Resource Allocation by Type (bar chart), Personnel Distribution by Role (bar chart), and other exercise metrics with interactive tooltips.',
      keywords: ['analytics', 'dashboard', 'charts', 'metrics', 'statistics', 'exercise']
    },
    {
      id: 31,
      category: 'Analytics',
      question: 'How do I read the Resource Allocation chart?',
      answer: 'The Resource Allocation by Type chart is a horizontal bar chart that groups resources by their category prefix (e.g., Vehicle, Equipment, Personnel, Aircraft, Watercraft). Hover over any bar to see the exact count. The chart uses orange-themed tooltips and percentage badges for quick comparison.',
      keywords: ['resource allocation', 'bar chart', 'type', 'category', 'analytics']
    },
    {
      id: 32,
      category: 'Analytics',
      question: 'What does the Personnel Distribution by Role chart show?',
      answer: 'The Personnel Distribution by Role chart is a bar chart showing how many participants are assigned to each ICS section: Command, Operations, Planning, Logistics, Finance, and Other. This helps identify staffing imbalances and ensure proper span of control across sections.',
      keywords: ['personnel', 'distribution', 'role', 'section', 'command', 'operations', 'planning', 'logistics', 'finance']
    },
    {
      id: 33,
      category: 'Evaluations & Lessons Learned',
      question: 'Where do I find Evaluations and Lessons Learned?',
      answer: 'Evaluations live on the Exercise Dashboard (per-exercise view) and Lessons Learned is now a promoted top-level menu item in the left sidebar. Both are scoped to the specific exercise you are viewing.',
      keywords: ['evaluations', 'lessons learned', 'menu', 'exercise', 'dashboard', 'sidebar']
    },
    {
      id: 34,
      category: 'Evaluations & Lessons Learned',
      question: 'How do I create an Evaluation report?',
      answer: 'From the Exercise Dashboard, open Evaluations and click "Add Evaluation". Fill in the criteria, ratings, observations, and recommendations. You can also attach supporting documents (doc, xls, pdf, ppt) using the multi-file upload in the Appendices field. The Save button is at the top of the form.',
      keywords: ['evaluation', 'create', 'add', 'report', 'assessment', 'rating', 'appendices', 'upload']
    },
    {
      id: 35,
      category: 'Evaluations & Lessons Learned',
      question: 'How do Lessons Learned work?',
      answer: 'Lessons Learned capture actionable insights from exercises. Open Lessons Learned from the sidebar, click "Add Lesson" and document what happened, what was expected, the root cause, and recommended corrective actions. Lessons are exercise-specific and do not leak between exercises.',
      keywords: ['lessons learned', 'create', 'add', 'corrective action', 'root cause']
    },
    {
      id: 36,
      category: 'Evaluations & Lessons Learned',
      question: 'Can I print Evaluation and Lessons Learned reports?',
      answer: 'Yes. Both Evaluations and Lessons Learned have print functionality using DOM-safe printing. Open the report and click the Print button at the top. The output is a clean, print-ready format suitable for after-action reviews and documentation.',
      keywords: ['print', 'evaluation', 'lessons learned', 'report', 'export']
    },
    {
      id: 37,
      category: 'Resource Management',
      question: 'How do I add a resource to an exercise?',
      answer: 'Navigate to the exercise, then go to the Resources section. Click "Add Resource" and fill in all required fields (marked with *): Resource Name, Type, Status, and other applicable fields. The form validates required fields and auto-scrolls to any errors. Click Save to add the resource.',
      keywords: ['resource', 'add', 'create', 'exercise', 'required fields']
    },
    {
      id: 38,
      category: 'Resource Management',
      question: 'What resource types are available?',
      answer: 'EXRSIM provides 57 categorized resource types organized by prefix: Vehicle (Ambulance, Command Vehicle, Fire Engine, etc.), Equipment (Generator, Hazmat Kit, Lighting, etc.), Personnel (EMT, Firefighter, Paramedic, etc.), Aircraft (Fixed Wing, Helicopter), Watercraft (Boat, Vessel), and more. Select from the dropdown when creating or editing a resource.',
      keywords: ['resource type', 'categories', 'vehicle', 'equipment', 'personnel', 'aircraft', 'dropdown', '57']
    },
    {
      id: 39,
      category: 'Resource Management',
      question: 'What do the resource status options mean?',
      answer: 'Resources can have the following statuses: Available (ready for deployment), Assigned (currently in use), Out of Service (not operational), En Route (traveling to assignment), and On Scene (at the incident location). Update status as resources move through the incident lifecycle.',
      keywords: ['resource status', 'available', 'assigned', 'out of service', 'en route', 'on scene']
    },
    {
      id: 40,
      category: 'Logistics Forms',
      question: 'What Logistics unit forms are available in EXRSIM?',
      answer: 'EXRSIM includes dedicated forms for each Logistics unit: Food Unit (meal planning and service), Facilities Unit (base camps, staging areas), and support forms that track physical resources. Access these from the Logistics section within an exercise.',
      keywords: ['logistics', 'food unit', 'facilities', 'unit forms', 'base camp', 'staging']
    },
    {
      id: 41,
      category: 'Logistics Forms',
      question: 'What Finance/Admin forms are available?',
      answer: 'The Finance/Administration section includes: Cost Unit (expense tracking), Time Unit (personnel hours and overtime), Compensation/Claims (injury and damage documentation), Procurement (purchasing and vendor management), and ICS 259 Financial Status Summary with four sub-forms for Crew, Equipment, Overhead, and Supplies costs.',
      keywords: ['finance', 'admin', 'cost unit', 'time unit', 'compensation', 'claims', 'procurement', 'ics 259']
    },
    {
      id: 42,
      category: 'Logistics Forms',
      question: 'How does the ICS 259 Financial Status Summary work?',
      answer: 'ICS 259 provides a consolidated financial view across four cost categories. Navigate to Finance/Admin > ICS 259 and complete each sub-form: Crew (personnel costs), Equipment (vehicle and equipment costs), Overhead (administrative costs), and Supplies (materials and consumables). Each sub-form has its own data entry and the totals feed into the summary. Use the Print button for a consolidated financial report.',
      keywords: ['ics 259', 'financial', 'summary', 'crew', 'equipment', 'overhead', 'supplies', 'cost']
    },
    {
      id: 43,
      category: 'Mapping & Tracking',
      question: 'How does the Resource Tracker map work?',
      answer: 'The Resource Tracker uses an interactive Leaflet map to display resource positions in real-time. Resources with GPS coordinates appear as markers on the map. You can zoom, pan, and click markers to see resource details. The map supports multiple layers including satellite view and terrain.',
      keywords: ['map', 'tracker', 'leaflet', 'gps', 'resource', 'position', 'location']
    },
    {
      id: 44,
      category: 'Mapping & Tracking',
      question: 'Can I draw on the incident map?',
      answer: 'Yes. The ICS map supports drawing various shapes: markers for points of interest, polygons for incident perimeters, polylines for routes, rectangles for zones, and circles for hazard areas. Each drawn object can have a label and description. These map objects are saved with the exercise.',
      keywords: ['draw', 'map', 'polygon', 'marker', 'perimeter', 'zone', 'shape', 'incident']
    },
    {
      id: 45,
      category: 'Mapping & Tracking',
      question: 'How do I view weather on the map?',
      answer: 'The ICS Dashboard Overview shows a Weather card next to the exercise map. It is powered by the GOES East/West satellite feed configured in the Weather Module (Settings → Weather) and silently refreshes every 2 hours. To change the feed, open Weather → pick the GOES East / West direction and then the GOES Image (Full Disk, CONUS, Canada regions, Arctic, etc.). The selected image is what appears on the Dashboard card.',
      keywords: ['weather', 'map', 'forecast', 'temperature', 'conditions', 'dashboard', 'goes', 'satellite']
    },
    {
      id: 46,
      category: 'AI Deputy IC',
      question: 'What is the AI Deputy IC and how do I use it?',
      answer: 'The AI Deputy IC (also called AI Assistant) is powered by Claude Sonnet 4.5 and grounded in the Canadian Incident Command System (ICS Canada / BCWS / CIFFC / Public Safety Canada FERP). Open it from the Settings dropdown in the top navigation → "AI Assistant" (the shield icon). It is enabled once you have selected an exercise.\n\n' +
        'The assistant automatically reads a live Situation Snapshot of your exercise — HIRA, objectives, positions, participants, and related ICS form data — and answers questions in plain language with the relevant ICS form or principle cited.\n\n' +
        'Each conversation is private to you and persists per exercise. Use the trash icon in the header to clear your conversation history at any time.',
      keywords: ['ai', 'deputy ic', 'claude', 'assistant', 'chatbot', 'canadian ics', 'sonnet', 'ai help', 'settings', 'ai assistant']
    },
    {
      id: 47,
      category: 'AI Deputy IC',
      question: 'What quick prompts can the AI Deputy IC handle out of the box?',
      answer: 'When you first open the AI Assistant, curated starter prompts appear with a single click:\n\n' +
        '• Summarise the current situation for a new arrival at Command\n' +
        '• What ICS 200/300 procedures apply to a wildland urban interface fire?\n' +
        '• Draft an initial ICS 201 briefing for this exercise\n' +
        '• What Canadian regulations govern mandatory evacuations?\n' +
        '• Recommend Section Chiefs given the objectives on file\n\n' +
        'You can also type any free-form question (e.g., "Should I activate Unified Command?", "What is my biggest unmet resource need?", "Compare current actions against ICS doctrine"). Shift+Enter inserts a newline; Enter sends.',
      keywords: ['ai', 'quick prompts', 'starter prompts', 'situation summary', 'iap briefing', 'deputy ic', 'ai assistant']
    },
    {
      id: 48,
      category: 'AI Deputy IC',
      question: 'Does the AI Deputy IC use FEMA NIMS or Canadian ICS?',
      answer: 'The AI Deputy IC is grounded exclusively in the Canadian Incident Command System. Its system prompt is built from ICS Canada, BC Wildfire Service (BCWS) ICS Operational Guidelines, CIFFC standards, Public Safety Canada Federal Emergency Response Plan (FERP), and provincial EMOs (EMBC, AEMA, etc.). It uses Canadian position titles (Incident Commander, Operations Section Chief, Safety Officer, etc.), recognizes Federal/Provincial/Municipal/Indigenous Nations jurisdictional layering (with Unified Command as the norm), and treats Canadian scenarios (wildfire interface, extreme cold, flooding, ice storms, marine SAR, etc.) as the default context.\n\n' +
        'It will not respond using FEMA-only terminology unless you explicitly ask for a cross-border mapping.',
      keywords: ['ai', 'canadian ics', 'fema', 'doctrine', 'bcws', 'ciffc', 'public safety canada', 'unified command']
    },
    {
      id: 49,
      category: 'AI Deputy IC',
      question: 'Where does the AI Deputy IC get its information? Is my data sent externally?',
      answer: 'Each time you send a message, EXRSIM builds a Situation Snapshot from your current exercise (objectives, assignments, org chart, resources, safety, schedule, activity log) and sends that snapshot together with your question to Anthropic\'s Claude Sonnet 4.5 model through the Emergent Universal LLM Key. Only data from your current exercise is included, scoped to the exercise you are viewing.\n\n' +
        'Conversations are stored in EXRSIM\'s database keyed by (exercise + your user account) — other users do not see your conversation. Always treat AI guidance as advisory and verify decisions against your agency SOPs.',
      keywords: ['ai', 'privacy', 'data', 'anthropic', 'claude', 'security', 'situation snapshot']
    },
    {
      id: 50,
      category: 'ICS Forms',
      question: 'How does the new ICS 230 Daily Meeting Schedule work?',
      answer: 'ICS 230 (Daily Meeting Schedule) has been overhauled. From Planning → Situation Unit → Daily Schedule you can:\n\n' +
        '• Add meetings with Date/Time From and Date/Time To (so each meeting has a real duration)\n' +
        '• Pick a Category Color from a dropdown (Briefing, Planning, Tactics, Operations, etc.) to colour-code the calendar\n' +
        '• Use Quick Assign drag-and-drop to add Personnel (chip list of attendees) and Resources directly from the sidebar\n' +
        '• Switch between Day, Week, Month, and Year views with the interactive react-big-calendar — click any event to edit, drag to move\n' +
        '• Export the schedule as an iCal (.ics) file or open it directly in Google Calendar via the "Add to Google Calendar" link\n\n' +
        'Meeting schedules are scoped to the current exercise and automatically tagged with your organization_id so they appear correctly for regular users.',
      keywords: ['ics 230', 'meeting schedule', 'calendar', 'ical', 'google calendar', 'export', 'daily schedule', 'quick assign']
    },
    {
      id: 51,
      category: 'ICS Forms',
      question: 'How do I export the ICS 230 schedule to my own calendar?',
      answer: 'Open Planning → Situation Unit → Daily Schedule, then use either:\n\n' +
        '• Download .ics — generates a standard iCalendar file that can be imported into Outlook, Apple Calendar, or any RFC-5545 compatible client.\n' +
        '• Add to Google Calendar — opens a Google Calendar event template prefilled with the meeting details.\n\n' +
        'The backend endpoint is GET /api/meeting-schedules/export.ics?exercise_id=… and it emits one VEVENT per meeting with the full date/time range and category.',
      keywords: ['ics 230', 'ical', 'export', 'google calendar', 'outlook', 'apple calendar', '.ics file']
    },
    {
      id: 52,
      category: 'ICS Forms',
      question: 'How does Quick Assign work on the ICS 204 Assignment List?',
      answer: 'When editing an ICS 204 assignment, click the "Quick Assign" button in the top-right to open the right-hand sidebar. The sidebar has two tabs:\n\n' +
        '• Resource Units — lists every Individual Unit (e.g. Engine-1, Rescue-12) available for the exercise. Drag a unit onto the "Resources Assigned" area to add it.\n' +
        '• Personnel — drag named participants onto an existing assignment to populate the Leader / Personnel fields.\n\n' +
        'Quick-Assigned resources render in a compact card showing only: thumbnail Image, Resource Type, Unit Name/ID, and # of Personnel — keeping the form readable. Resources added the legacy way still show the full row.',
      keywords: ['ics 204', 'quick assign', 'drag drop', 'assignment', 'individual unit', 'compact view', 'resources']
    },
    {
      id: 53,
      category: 'Resource Management',
      question: 'What is the Two-Tier Resource Model (Parent Types vs. Individual Units)?',
      answer: 'EXRSIM resources are organised into two tiers:\n\n' +
        '• Parent Resource Type — the category template (e.g. "Aircraft Fixed Wing: Search and Rescue", "Type 3 Engine", "Paramedic"). Defines shared attributes: type, image, capabilities, typical personnel count, etc.\n' +
        '• Individual Unit — a concrete instance of a Parent Type with its own callsign / Unit ID, status, current location, and assignment (e.g. TEST-Unit-002, Engine-1, Rescue-12). These are what actually get assigned to ICS 204 work, tracked on the map, and counted in analytics.\n\n' +
        'Quick Assign on ICS 204 and ICS 230 pulls from Individual Units (not Parent Types). The Analytics "Resources by Type" chart counts Individual Units per type to give an accurate deployment picture rather than a count of templates.',
      keywords: ['two-tier', 'parent resource', 'individual unit', 'callsign', 'unit id', 'resource model', 'analytics']
    },
    {
      id: 54,
      category: 'Analytics',
      question: 'What changed in the "Resources by Type" analytics chart?',
      answer: 'The Resources by Type bar chart now counts Individual Units per Parent Resource Type rather than counting Parent Types themselves. This gives a far more accurate view of deployable strength (e.g. it now shows "Type 3 Engine: 6 units" rather than "Type 3 Engine: 1 template"). The chart pulls live from the Individual Units associated with each parent type for the selected exercise.',
      keywords: ['analytics', 'resources by type', 'individual unit', 'bar chart', 'count', 'deployment']
    },
    {
      id: 55,
      category: 'Safety',
      question: 'Where did the "Safety" sidebar menu go? Where is ICS 208 now?',
      answer: 'The standalone "Safety" / "Safety Management" sidebar menu has been removed. ICS 208 (Safety Message / Plan) is now reached from Command Staff → Safety Officer, which is its proper organisational home under Canadian ICS doctrine.\n\n' +
        'The Safety Officer module itself has been reworked: "Incident Findings & Recommendations" is now simply "Incident Findings"; new Safety Message and Safety Plan fields are first-class entries; the Notes field has been removed; and the Print Safety Officer Report now includes every field from every entry.',
      keywords: ['safety', 'safety officer', 'ics 208', 'incident findings', 'safety message', 'safety plan', 'sidebar', 'menu']
    },
    {
      id: 56,
      category: 'Operations',
      question: 'Where is the Air Ops menu? It seems to have moved.',
      answer: 'Air Ops has been moved to sit directly below "Incident Status" (ICS 209) under the Operations section, reflecting its operational adjacency to incident status reporting. The submenu still contains "Air Ops Summ." Use the Operations menu group in the ICS sidebar to find it.',
      keywords: ['air ops', 'air operations', 'menu', 'sidebar', 'operations']
    },
    {
      id: 57,
      category: 'User Management',
      question: 'How do admins enable or disable 2FA for another user?',
      answer: 'Both Agency Admins (within their own agency) and Super Admins (across all agencies) can toggle two-factor authentication for any user from User Management:\n\n' +
        '1. Navigate to Settings → User Management.\n' +
        '2. Find the user in the list and click Edit.\n' +
        '3. Toggle the "Two-Factor Authentication" switch on/off and save.\n\n' +
        'Disabling 2FA clears the user\'s TOTP secret. The next time they log in, no 2FA challenge is shown. Re-enabling prompts them to scan a fresh QR code and provide a verification token.',
      keywords: ['2fa', 'two-factor', 'mfa', 'user management', 'agency admin', 'super admin', 'security', 'toggle']
    },
    {
      id: 58,
      category: 'Safety',
      question: 'What is included in the Safety Officer Print Report?',
      answer: 'The Safety Officer Report (Print button on the Safety Officer module) now includes every field from every entry: Incident Findings, Recommendations, Safety Message, Safety Plan, the date/time of each entry, and the entering user. The previously truncated layout has been replaced with a full audit-quality printout suitable for after-action review.',
      keywords: ['safety officer', 'print', 'report', 'incident findings', 'safety message', 'safety plan', 'after action']
    },
    {
      id: 59,
      category: 'Getting Started',
      question: 'What\'s new in the latest EXRSIM release?',
      answer: 'Recent additions (Feb 16, 2026):\n\n' +
        '• Click-to-open native date picker — every <input type="date|datetime-local|time|month|week"> across the platform (Lessons Learned, ICS 205/206/213/221/224/230/259/309, ExCon Builder, Safety, all Unit forms, etc.) now opens its picker on click or focus anywhere in the field. The small calendar icon is hidden; the whole field is the click target with a pointer cursor.\n' +
        '• Light Theme (brand-safe) — toggle Sun/Moon icon in the top navigation bar. All dark Tailwind backgrounds, text colours, borders, placeholders, scrollbars, form inputs, and shadows switch to a slate-based light palette. Orange EXRSIM brand accent, ICS priority (red/amber/green/blue), status (green/red), and DOTMPLFICC colours are preserved in both themes.\n' +
        '• Lessons Learned polish — Serial Numbers auto-increment per exercise (field is disabled + marked "Auto-numbered per exercise"); missing required fields render with a bold red border + red ring and the page auto-scrolls to the first invalid field; new Expand All / Collapse All buttons plus a per-card chevron; priority colour bar on the left and Status pill on the right; Category / ICS Organization / Status consolidated onto a single row on the Observations & Recommended Actions card.\n' +
        '• Participants Check-In — collapsed list now has two checkboxes per row: "Selected" (for bulk operations) and "Checked In" (for attendance tracking). Toolbar adds "All Check In" / "All Check Out" bulk buttons that apply to the currently filtered view with optimistic UI.\n' +
        '• Participants: Languages & Skills / Quals — Personal Information card now captures a "Languages" field (right of Position) and a "Skills / Quals" textarea (below Home Phone), persisted on the Participant model and visible on the collapsed list.\n' +
        '• Ottawa Snow Storm — 10 multilingual mock participants (IC, Deputy IC, Section Chiefs, PIO, Safety, Liaison, Resource Unit Leader) are seeded idempotently via `backend/tests/seed_ottawa_snow_participants.py`.\n\n' +
        'Previous release (Feb 22, 2026):\n\n' +
        '• ICS 213 – General Messages — moved under the Comms Unit, dashboard header renamed to "ICS 213 - General Messages", full CRUD, expand/collapse UX, color-coded Priority (Routine/Priority/Immediate/Flash) and Category selects, and unified split Date + 24-hour HH:MM time.\n' +
        '• ICS 213 WinLink CSV parity — per-row Export CSV plus bulk Import/Export endpoints. Imports are idempotent (upsert on Subject+Date+Time). Sample file at /sample_ics213_winlink.csv.\n' +
        '• ICS 213 "Print ICS 213 Report" — DOM-safe consolidated print of every expanded entry from the top of the dashboard.\n' +
        '• ICS 309 Comms Log CSV — Import/Export round-trip with idempotent upsert on RadioNetwork+OperationalPeriod. Sample file at /sample_ics309_commslog.csv.\n' +
        '• ICS 205 Comms Freqs CSV — Import/Export round-trip with idempotent upsert on Channel+RxFreq. Dashboard renamed to "Comms Freqs". Sample file at /sample_ics205_freqs.csv.\n' +
        '• After Action Report — annex order reshuffled to A. Incident Tracker, B. Participants, C. Resources, D. Financials, E. Activity Log, F. Comms Log (both on-screen and in print output).\n' +
        '• Exercise Evaluations overhaul — reworked to the ICS-standard 9 Key Areas (Command & Control, Operations, Planning, Logistics, Finance/Admin, Communication, Resource Management, Safety & Security, Training & Readiness). Ratings are now color-coded radio buttons (Excellent → Unacceptable + Not Assessed). Not Assessed is excluded from the Overall Rating average. Each Key Area prints on its own full-width row.\n' +
        '• Login/Register background — full-bleed firefighters image behind the auth cards.\n' +
        '• "Wx Obs" menu renamed to "Weather Obs" for clarity.\n' +
        '• GOES East/West feeds — auto-migrate on backend startup so production always self-heals.\n\n' +
        'Previous release (Feb 2026 fork): DOM-safe printing across ICS forms, Print MSEL from list & modal, Map Display Mode auto-bound, split Date + 24-hour Time everywhere, Organization ↔ Liaison persons sync, Exercise Scope (Step 4) cleanup, Supply Unit form_type isolation, AI Deputy IC via Settings → AI Assistant.\n' +
        'Earlier release (May 2026): AI Deputy IC (Claude Sonnet 4.5), ICS 230 calendar + iCal, ICS 204 Quick Assign, Two-Tier Resource Model, Safety Officer rework, 2FA admin toggle, sidebar cleanup.',
      keywords: ['whats new', 'release', 'changelog', 'updates', 'latest', 'feb 2026', 'recent', 'new features', 'date picker', 'light theme', 'theme toggle', 'lessons learned', 'checked in', 'languages', 'skills quals', 'ics 213', 'winlink', 'csv', 'comms log', 'comms freqs', 'aar', 'evaluations', 'weather obs']
    },
    {
      id: 60,
      category: 'MSEL',
      question: 'How do I print the entire MSEL for an exercise?',
      answer: 'You can print the full Master Scenario Events List two ways — both use the same DOM-safe print writer, so the output is identical and includes every injection with Scenario Date/Time, From, To, Event Type, Inject Mode, Message, Expected Response, and Actual Response:\n\n' +
        '• From the Exercises menu → find the exercise → click the "Print MSEL" button in the row/card.\n' +
        '• From an open exercise → open the MSEL modal in the Exercise Detail view → click the "Print MSEL" button in the modal header.\n\n' +
        'The output is a clean, sanitised HTML page ready for the browser print dialog (Ctrl/⌘+P → Save as PDF works too).',
      keywords: ['msel', 'print', 'injections', 'exercise', 'pdf', 'dom-safe', 'master scenario events list']
    },
    {
      id: 61,
      category: 'Printing',
      question: 'How does DOM-safe printing work in EXRSIM?',
      answer: 'All list-based ICS forms in EXRSIM now generate their print output through a single sanitising helper (`utils/safePrint.js` → `writeSanitizedHTML`). It uses DOMPurify to strip potentially unsafe markup and inline `style` attributes, then wraps the result in a print-ready page with a `<style>` block for filled radio buttons, tables, and card layouts.\n\n' +
        'What this means for you as a user:\n' +
        '• Print output is consistent across After Action Report, ICS 224, ICS 225, ICS 309, Food Unit, Facilities Unit, and MSEL.\n' +
        '• Every card / row / entry is included — no more truncated pages.\n' +
        '• Checkboxes and rating radio buttons render as filled CSS circles (so they survive DOMPurify\'s inline-style stripping).\n' +
        '• Printing does not mutate on-screen form state.',
      keywords: ['print', 'dom-safe', 'dompurify', 'sanitized', 'export', 'after action', 'ics forms', 'safe print']
    },
    {
      id: 62,
      category: 'Weather',
      question: 'How does the GOES Satellite weather feed work?',
      answer: 'The Weather Module (Settings → Weather) now serves GOES East / West satellite imagery instead of the previous Canadian city RSS list. Workflow:\n\n' +
        '1. Choose GOES East / West — the direction/satellite.\n' +
        '2. Choose GOES Image — the region (Full Disk, CONUS, Canadian regions, Arctic, etc.).\n' +
        '3. The selected image URL is the RSS/Feed URL used across EXRSIM. Copy it with the Copy button next to the URL field.\n\n' +
        'The ICS Dashboard → Weather card silently auto-refreshes every 2 hours to keep the current GOES image up to date without any user action. The catalogue is seeded via the backend endpoint `POST /api/weather-locations/import-excel`.',
      keywords: ['goes', 'weather', 'satellite', 'east', 'west', 'full disk', 'conus', 'canada', 'arctic', 'auto refresh', '2 hour']
    },
    {
      id: 63,
      category: 'Mapping & Tracking',
      question: 'Does the Display Mode map auto-center on my exercise objects?',
      answer: 'Yes. The ICS Dashboard → Display Mode map now uses a `FitBoundsToObjects` calculator: it walks every geo-object on the current exercise (markers, circles, lines, rectangles, polygons — regardless of GeoJSON shape) and fits the viewport to encompass all of them.\n\n' +
        'If your exercise has no geo-objects yet, the map defaults to a sensible Canadian view; otherwise it auto-zooms/pans to the smallest bounds that show every object. This replaces the old behaviour that hard-centered on Ottawa.',
      keywords: ['display mode', 'map', 'auto center', 'auto bound', 'fit bounds', 'markers', 'polygons', 'circles', 'ottawa']
    },
    {
      id: 64,
      category: 'Organizations & Liaison',
      question: 'Why does editing an Organization no longer wipe its Persons?',
      answer: 'A bug was fixed where editing an organization from Step 8 (Organizations) of the Exercise Builder cleared the `persons` array. The fix:\n\n' +
        '• Auto-persists the full organization payload — including `persons` — on every edit.\n' +
        '• Adds a `useEffect` re-fetch so Step 8 always displays the same data as the Liaison Officer module.\n' +
        '• Keeps the two views (Builder Step 8 ↔ Liaison Officer) synchronised in real time.\n\n' +
        'You can safely edit an organization from either place without losing its people or their contact information.',
      keywords: ['organization', 'persons', 'liaison', 'step 8', 'auto-persist', 'sync', 'bug fix', 'wiped']
    },
    {
      id: 65,
      category: 'Getting Started',
      question: 'What is on the redesigned Exercise Scope (Step 4)?',
      answer: 'Step 4 (Scope) has been cleaned up so it no longer duplicates fields from other steps. It now contains only the Scope Description. The following fields have been removed from Step 4 because they already live in their canonical step:\n\n' +
        '• Hazards → HIRA / Step 2 Scenario\n' +
        '• Geographical Area → Step 17 Mapping\n' +
        '• Functions → Step 6 Functions\n' +
        '• Exercise Type → Step 1 Exercise Details\n\n' +
        'Any existing exercises retain whatever data was previously entered, but new exercises fill these fields only in their canonical steps.',
      keywords: ['scope', 'step 4', 'exercise builder', 'hazards', 'geographic area', 'functions', 'exercise type', 'cleanup']
    },
    {
      id: 66,
      category: 'ICS Forms',
      question: 'Where do I find the new "ICS 213 - General Messages" dashboard?',
      answer: 'ICS 213 – General Messages is now located under the Comms Unit. Two ways to reach it:\n\n' +
        '• Sidebar → Comms Unit → General Message\n' +
        '• ICS 2XX Form Series hub → click the "ICS 213 – General Message" card\n\n' +
        'The dashboard header reads "ICS 213 - General Messages". Each row is collapsible; use "Expand All" / "Collapse All" at the top to switch between summary and detail views. Priority and Category badges are color-coded (Routine, Priority, Immediate, Flash / Info, Request, Directive, Ack, etc.). Date and Time are captured in split fields — the time uses 24-hour HH:MM.',
      keywords: ['ics 213', 'general messages', 'comms unit', 'dashboard', 'priority', 'category', 'winlink']
    },
    {
      id: 67,
      category: 'CSV Import / Export',
      question: 'How do I import or export ICS 213 messages via WinLink CSV?',
      answer: 'ICS 213 supports full WinLink-compatible CSV round-trip:\n\n' +
        '• Per-row Export CSV — click the CSV button on any single message card to download that one message as an RFC 4180 escaped CSV (client-side).\n' +
        '• Bulk Export CSV — top of dashboard. Downloads every ICS 213 message on the current exercise using the WinLink column order (Category, Precedence, Msg #, Date, Time, To, From, Subject, Message, Incident Name, …).\n' +
        '• Bulk Import CSV — top of dashboard. Accepts WinLink-format files with case-insensitive headers and alias fallbacks. The importer is idempotent — records match on the composite key (exercise_id + Subject + Date + Time) and are updated rather than duplicated on re-import.\n\n' +
        'A sample WinLink CSV lives at /sample_ics213_winlink.csv for testing.',
      keywords: ['ics 213', 'winlink', 'csv', 'import', 'export', 'round-trip', 'idempotent']
    },
    {
      id: 68,
      category: 'CSV Import / Export',
      question: 'Can I import / export the ICS 309 Comms Log via CSV?',
      answer: 'Yes. The ICS 309 Comms Log dashboard has both Import CSV and Export CSV buttons alongside the Print button:\n\n' +
        '• Export CSV — dumps every Radio Network / Operational Period on the exercise, one row per message_log entry (Msg#, Date, Time, From, To, Message, Action).\n' +
        '• Import CSV — idempotent upsert on (exercise_id + RadioNetwork + OperationalPeriod). Message log entries are merged by Msg# so a re-import updates existing rows rather than duplicating them.\n\n' +
        'A sample file lives at /sample_ics309_commslog.csv.',
      keywords: ['ics 309', 'comms log', 'communications log', 'csv', 'import', 'export', 'idempotent', 'upsert']
    },
    {
      id: 69,
      category: 'CSV Import / Export',
      question: 'How does the ICS 205 Comms Freqs CSV round-trip work?',
      answer: 'The ICS 205 dashboard (now labelled "Comms Freqs") supports full CSV Import/Export:\n\n' +
        '• Export CSV — one row per frequency entry with Channel, Function, RX Freq, RX Tone/NAC, TX Freq, TX Tone/NAC, Mode, Assignment, Remarks.\n' +
        '• Import CSV — idempotent upsert on (exercise_id + Channel + RxFreq). Re-importing an existing file updates rows in place instead of duplicating them.\n\n' +
        'A sample file lives at /sample_ics205_freqs.csv. Column headers are matched case-insensitively with fallback aliases so raw exports from WinLink / other radio programming tools generally work without renaming columns.',
      keywords: ['ics 205', 'comms freqs', 'frequencies', 'csv', 'import', 'export', 'winlink']
    },
    {
      id: 70,
      category: 'After Action Report',
      question: 'What is the current annex order in the After Action Report?',
      answer: 'The After Action Report annexes were reshuffled to reflect logical review flow. Current order (both on-screen and in print output):\n\n' +
        '• Annex A — Incident Tracker\n' +
        '• Annex B — Participants\n' +
        '• Annex C — Resources\n' +
        '• Annex D — Financials\n' +
        '• Annex E — Activity Log (ICS 214 timeline)\n' +
        '• Annex F — Communications Log (ICS 309)\n\n' +
        'Financials sits mid-report ahead of the two chronological annexes so reviewers can see cost commitments before diving into blow-by-blow activity and comms.',
      keywords: ['after action', 'aar', 'annex', 'order', 'incident tracker', 'participants', 'resources', 'financials', 'activity log', 'comms log']
    },
    {
      id: 71,
      category: 'Evaluations & Lessons Learned',
      question: 'What changed in the Exercise Evaluations module?',
      answer: 'Exercise Evaluations were reworked to align with the ICS-standard 9 Key Areas and to modernise the rating UX:\n\n' +
        '• 9 Key Areas assessed on every report — Command & Control, Operations, Planning, Logistics, Finance / Admin, Communication, Resource Management, Safety & Security, Training & Readiness. (The old "Operational Effectiveness" and "Plan Adherence / Adaptability" fields have been retired.)\n' +
        '• Ratings are now color-coded radio buttons on a single row: Excellent (green) → Above Average → Average → Below Average → Unacceptable (red) → Not Assessed (grey).\n' +
        '• Overall Rating is calculated as the average of the 9 areas, EXCLUDING any area marked "Not Assessed". If every area is Not Assessed, Overall Rating displays "Not Assessed".\n' +
        '• Print output — each Key Area assessment now prints on its own full-width row (no more 2-column grid squashing content).\n' +
        '• Legacy reports — older entries in the database that still have `operational_effectiveness` / `plan_adherence_adaptability` fields are read gracefully; the four new fields default to "Average" on those records until an editor saves them.',
      keywords: ['evaluations', 'ics 9 key areas', 'radio buttons', 'color coded', 'not assessed', 'overall rating', 'excellent', 'unacceptable']
    },
    {
      id: 72,
      category: 'Getting Started',
      question: 'Why does the Login / Register screen have a firefighters background?',
      answer: 'The auth screens (Login and Register) now render with a full-bleed firefighters photograph behind the auth card as part of the platform refresh. It replaces the older solid background and reinforces the emergency-services identity of EXRSIM. Functionality on both screens is unchanged.',
      keywords: ['login', 'register', 'background', 'firefighters', 'ui', 'branding']
    },
    {
      id: 73,
      category: 'Navigation',
      question: 'What happened to the "Wx Obs" menu?',
      answer: 'The sidebar menu "Wx Obs" has been renamed to "Weather Obs" for clarity. Functionality is unchanged — it still opens the weather observations module.',
      keywords: ['wx obs', 'weather obs', 'menu', 'rename', 'sidebar', 'weather']
    },
    {
      id: 74,
      category: 'Weather',
      question: 'Do I still need to manually seed the GOES weather feeds after a production deploy?',
      answer: 'No. GOES East/West satellite feeds are now auto-seeded on backend startup via an idempotent `@app.on_event("startup")` hook in `server.py`. On every FastAPI boot, the backend checks the `weather_locations` collection and installs or updates the curated GOES East/West feed list. Production naturally self-heals — no manual call to `POST /api/weather-locations/import-excel` is required after a deploy.',
      keywords: ['goes', 'weather', 'auto-migration', 'startup', 'self-heal', 'production', 'deploy']
    },
    {
      id: 75,
      category: 'Date/Time',
      question: 'How do I open the native date / time picker now?',
      answer: 'Click anywhere in the date, time, datetime, month, or week field — the native browser picker opens immediately. The small calendar icon that used to sit on the right has been hidden, and the whole field is the click target (your cursor becomes a pointer when you hover over it). Tab-focusing into a field with the keyboard also opens the picker.\n\nThis works across every ICS form in EXRSIM — Lessons Learned, ICS 205/206/213/221/224/230/259/309, ExCon Builder, Safety Officer, Food / Facilities / Cost / Time / Resource Unit forms — without you having to change anything per screen. Browser support: Chrome / Edge 99+, Firefox 101+, Safari 16+. On older browsers the field still works like a normal date input.',
      keywords: ['date picker', 'calendar', 'click to open', 'full field click', 'native picker', 'date field', 'time field', 'showPicker']
    },
    {
      id: 76,
      category: 'Theme & UI',
      question: 'How do I switch between Light and Dark theme?',
      answer: 'Click the Sun / Moon icon in the top navigation bar (next to your user menu). The entire EXRSIM UI toggles between Light and Dark instantly — all dark backgrounds, text colours, borders, placeholders, scrollbars, form inputs, and shadows switch to a slate-based light palette (or back).\n\nYour theme preference is remembered per browser.\n\nBrand & ICS colours are deliberately preserved in both themes:\n• Orange EXRSIM brand accent\n• ICS priority colours (red / amber / green / blue)\n• Status colours (green / red)\n• DOTMPLFICC section colours\n\nSo priority bars, status pills, section badges, and the EXRSIM logo look identical in Light and Dark mode — only the surrounding chrome changes.',
      keywords: ['theme', 'light theme', 'dark theme', 'sun moon', 'toggle', 'top nav', 'brand colors', 'ics colors', 'preference']
    },
    {
      id: 77,
      category: 'Evaluations & Lessons Learned',
      question: 'How do serial numbers work in Lessons Learned now?',
      answer: 'Serial Numbers are automatically assigned per exercise. On the Add / Edit Lesson form, the Serial Number field is disabled and labelled "Auto-numbered per exercise" — the backend assigns the next available integer when you save, so you never have to guess the next value or worry about collisions.\n\nIf you ever open the Lessons Learned panel from the ICS Sidebar before an exercise is selected, a red banner appears warning you that no `exerciseId` is set — this prevents silent 422 errors on Save. Open an exercise first, then return to Lessons Learned.',
      keywords: ['serial number', 'auto number', 'lessons learned', 'auto increment', 'exercise id', 'red banner']
    },
    {
      id: 78,
      category: 'Evaluations & Lessons Learned',
      question: 'How does Expand / Collapse work in Lessons Learned?',
      answer: 'The Lessons Learned list always opens fully collapsed so you see one summary row per lesson. From the toolbar you can:\n\n• Expand All — opens every card at once (References, Issues/Observation, Recommendations become visible).\n• Collapse All — slams every card back to summary mode.\n• Per-card chevron — click the chevron on any single card to expand or collapse just that one.\n\n"Select for Print" is now an explicit link, so clicking a collapsed card only toggles its expand state (not its print selection). After saving a new lesson, the list refreshes in collapsed mode so you can scan the full list before diving back into details.',
      keywords: ['expand all', 'collapse all', 'chevron', 'lessons learned', 'select for print', 'summary view']
    },
    {
      id: 79,
      category: 'Evaluations & Lessons Learned',
      question: 'What are the red borders I see on Lessons Learned?',
      answer: 'Required fields that are missing when you click Save now render with a bold red border and a soft red glow / ring. The page auto-scrolls to the first invalid field so you do not have to hunt for what is missing. Required fields on a Lesson are: Name, References, Issues / Observation, Recommendations, Status, and Stage.\n\nAdditionally, the Lesson card UI now puts:\n• A priority colour bar on the LEFT edge of each card (Pri 1 red → Pri 4 blue).\n• The Status pill on the RIGHT.\n• Category, ICS Organization, and Status all on the SAME row on the Observations & Recommended Actions card for faster scanning.',
      keywords: ['red border', 'validation', 'required fields', 'auto scroll', 'lessons learned', 'priority bar', 'status pill', 'category', 'ics organization']
    },
    {
      id: 80,
      category: 'Personnel',
      question: 'How do I track who has checked in / checked out of an exercise?',
      answer: 'The Participants list (collapsed view) now has TWO checkboxes per row:\n\n• Selected — the existing checkbox used for bulk operations (print, assign, etc.).\n• Checked In — a new checkbox dedicated to attendance tracking at the start of an exercise.\n\nFor fast attendance taking, the toolbar adds two bulk buttons:\n• All Check In — marks every currently-visible / filtered participant as Checked In.\n• All Check Out — clears the Checked In flag on every currently-visible / filtered participant.\n\nUpdates are optimistic (immediate UI feedback) with automatic revert on failure, and persist to the Participant record via `PUT /api/participants/{participant_id}`.',
      keywords: ['check in', 'check out', 'checked in', 'attendance', 'participants', 'bulk check in', 'selected', 'optimistic ui']
    },
    {
      id: 81,
      category: 'Personnel',
      question: 'Where do I enter a participant\'s languages and skills?',
      answer: 'On the Participant form → Personal Information card:\n\n• Languages — new field to the right of Position. Capture the languages the participant can work in (e.g. "English, French, Michif, ASL"). Useful for Liaison planning and multilingual public messaging via PIO.\n• Skills / Quals — new textarea below the Home Phone row. Capture certifications, qualifications, or specialised skills (ICS 200/300, HAZMAT Tech, First Aid Level 3, Red Cross Shelter Ops, etc.).\n\nBoth fields are stored on the Participant model (`languages`, `skillsQuals`) and are available for printing / future search.',
      keywords: ['languages', 'skills', 'quals', 'qualifications', 'participants', 'personal information', 'liaison', 'pio']
    }
  ],

  // Comprehensive Glossary - 241 ICS/Emergency Management Terms
  // Note: This glossary is fetched from the backend but stored here for reference
  // To update, modify GLOSSARY_TERMS in backend/server.py
  glossary: []  // Will be populated from backend API at /api/glossary
};

// Fuzzy search function for smart search
export const fuzzySearch = (searchTerm, items, keys) => {
  if (!searchTerm) return items;
  
  const lowerSearchTerm = searchTerm.toLowerCase();
  const searchWords = lowerSearchTerm.split(' ').filter(word => word.length > 0);
  
  return items.filter(item => {
    return searchWords.every(word => {
      return keys.some(key => {
        const value = key.split('.').reduce((obj, k) => obj?.[k], item);
        return value && value.toString().toLowerCase().includes(word);
      });
    });
  }).sort((a, b) => {
    // Prioritize exact matches
    const aExact = keys.some(key => {
      const value = key.split('.').reduce((obj, k) => obj?.[k], a);
      return value && value.toString().toLowerCase() === lowerSearchTerm;
    });
    const bExact = keys.some(key => {
      const value = key.split('.').reduce((obj, k) => obj?.[k], b);
      return value && value.toString().toLowerCase() === lowerSearchTerm;
    });
    
    if (aExact && !bExact) return -1;
    if (!aExact && bExact) return 1;
    return 0;
  });
};

export default helpContent;
